import './../../Player.css';
import React, {useContext, useState, useEffect, useRef} from 'react';
import * as Sentry from "@sentry/react";
import { useTranslation } from 'react-i18next';
import { PlayerContext } from '../../PlayerContext';
import IconButton from '@mui/material/IconButton';
import {Image} from 'react-native';
import { browserLocales, checkMobile, getTextWidth, isAndroid, isFacebookInAppBrowser, isFirefox, isIOS, isInstagramInAppBrowser, isSnapchatInAppBrowser, isTikTokInAppBrowser, printObj, addOpacityToHexColor, isIpad, isCafeUprealityDemoStoryId } from '../../Utils';
import { getDoc, doc, getFirestore } from 'firebase/firestore';
import MealDetail from './MealDetail';
import { analyticsManager } from '../../..';
import CloseButton from '../../side_menu/components/CloseButton';
import { StickerContext } from '../../StickerContext';
import { ChevronLeft, ChevronRight, Close } from '@mui/icons-material';
import AllergensSelect from '../AllergensSelect';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { LightTooltip } from './LightToolTip';
import CloseIcon from '@mui/icons-material/Close';
import ColorContrastChecker from 'color-contrast-checker';
import { Alert, Snackbar } from '@mui/material';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { getMenuFontStyle, getMenuDescFontStyle } from '../../menuFontLoader';
import { getStoredPixelConsent } from '../../PixelConsentOverlay';

function Menu(props){
    const { t } = useTranslation();
    const [pageNumber, setPageNumber] = useState(0);
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    const [touchStartY, setTouchStartY] = useState(null);
    const [touchEndY, setTouchEndY] = useState(null);
    const [menu, setMenu] = useState(null);    
    const [menuPages, setMenuPages] = useState(null);
    const [pageEntry, setPageEntry] = useState(null);
    const [filteredAllergens, setFilteredAllergens] = useState("");
    const [menuHasAllergens, setMenuHasAllergens] = useState(false);

    const language = (browserLocales(true)[0] === "en" || props.editorLanguage === "en") ? "en" : "fr";

    function getLocalizedMenuText(value) {
        if (value == null) {
            return "";
        }
        if (typeof value === "string") {
            return value;
        }
        return value[language] ?? value.fr ?? value.en ?? "";
    }

    function isRichTextMenuPage(page) {
        return String(page?.pageType?.fr || "").trim().toLowerCase() === "richtext";
    }

    /** HTML léger pour pages richtext : p/br/listes + gras/italique + span color/font-size. */
    function sanitizeMenuRichTextHtml(html) {
        if (!html) return "";
        return String(html)
            .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
            .replace(/on\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
            .replace(/<\/?([a-z0-9]+)(\s[^>]*)?>/gi, (match, rawTag, attrs = "") => {
                const tag = String(rawTag || "").toLowerCase();
                const allowed = ["p", "br", "ul", "ol", "li", "div", "strong", "b", "em", "i", "u", "span"];
                if (!allowed.includes(tag)) return "";
                if (match.startsWith("</")) return `</${tag}>`;
                if (tag === "br") return "<br>";
                if (tag === "span") {
                    const styleMatch = String(attrs).match(/style\s*=\s*["']([^"']*)["']/i);
                    if (styleMatch) {
                        const styleParts = [];
                        const colorMatch = styleMatch[1].match(/(?:^|;)\s*color\s*:\s*([^;]+)/i);
                        const fontSizeMatch = styleMatch[1].match(/(?:^|;)\s*font-size\s*:\s*([^;]+)/i);
                        if (colorMatch) {
                            const color = colorMatch[1].trim();
                            if (
                                /^#[0-9a-fA-F]{3,8}$/.test(color)
                                || /^rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)$/i.test(color)
                                || /^rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*[\d.]+\s*\)$/i.test(color)
                                || /^[a-zA-Z]{1,20}$/.test(color)
                            ) {
                                styleParts.push(`color:${color}`);
                            }
                        }
                        if (fontSizeMatch) {
                            const fontSize = fontSizeMatch[1].trim();
                            if (/^\d+(\.\d+)?(px|em|rem|%)$/i.test(fontSize)) {
                                styleParts.push(`font-size:${fontSize}`);
                            }
                        }
                        if (styleParts.length) {
                            return `<span style="${styleParts.join(";")}">`;
                        }
                    }
                    return "<span>";
                }
                return `<${tag}>`;
            });
    }

    function getLocalizedMenuPrices(value) {
        if (value == null) {
            return null;
        }
        if (Array.isArray(value)) {
            return value;
        }
        return value[language] ?? value.fr ?? value.en ?? null;
    }

    const windowDimensions = useContext(PlayerContext).get("windowDimensions");
    const storyDimensions = useContext(PlayerContext).get("storyDimensions");

    const menuColor = useContext(PlayerContext).get("menuColor");
    const stickerContext = useContext(StickerContext);
    const titleFont = stickerContext.get("menuTitleFont");
    const menuTitleFontFallback = stickerContext.get("menuTitleFontFallback");
    const titleFontSizeMult = titleFont === "Bestro" ? 2 : 1;
    const textFont = stickerContext.get("menuTextFont");
    const menuTextFontFallback = stickerContext.get("menuTextFontFallback");
    const descFont = stickerContext.get("menuDescFont");
    const menuDescFontFallback = stickerContext.get("menuDescFontFallback");
    // Comme avant : utiliser le nom du doc menu en attendant le chargement context.
    const menuTextFontName = props.story.menuStyle === "custom" && menu?.textFont?.name ? menu.textFont.name : null;
    const menuDescFontName = props.story.menuStyle === "custom" && menu?.descFont?.name ? menu.descFont.name : null;
    const resolvedTextFontFamily = textFont || menuTextFontName || (menuTextFontFallback ? "Roboto" : null);
    const resolvedDescFontFamily =
        descFont || menuDescFontName || (menuDescFontFallback ? "Roboto" : null) || resolvedTextFontFamily;
    const titleFontStyle = getMenuFontStyle(titleFont, menuTitleFontFallback);
    const textFontStyle = getMenuFontStyle(textFont || menuTextFontName, menuTextFontFallback);
    const descFontStyle = getMenuDescFontStyle({
        descFont,
        descFontNameFromMenu: menuDescFontName,
        descFontFallback: menuDescFontFallback,
        textFontStyle,
    });
    const titleColorStyle = props.story.menuStyle === "custom" && menu && menu.titleColor ? {color: menu.titleColor} : {}; 
    const priceColorStyle = props.story.menuStyle === "custom" && menu && menu.priceColor ? {color: menu.priceColor} : {}; 
    const descColorStyle = props.story.menuStyle === "custom" && menu && menu.descColor ? {color: menu.descColor} : {}; 
    const categoryFontStyleLikeTitle = props.story.menuStyle === "custom" && menu && menu.categoryFontStyleLikeTitle ? menu.categoryFontStyleLikeTitle : false;
    const hideMenuDescription = props.story.menuStyle === "custom" && menu?.hideMenuDescription === true;
    const titleFontBoldStyle = props.story.menuStyle === "custom" && menu?.titleFontBold ? { fontWeight: "bold" } : {};
    const textFontBoldStyle = props.story.menuStyle === "custom" && menu?.textFontBold ? { fontWeight: "bold" } : {};
    const playMode = useContext(PlayerContext).get("playMode");
    const sizeMultiplier = useContext(PlayerContext).get("sizeMultiplier");
    const parsedTextFontSize = props.story.menuStyle === "custom" && menu?.textFontSize != null && menu.textFontSize !== ""
      ? Number(menu.textFontSize)
      : NaN;
    const entryTitleFontSizePx = (Number.isFinite(parsedTextFontSize) && parsedTextFontSize > 0 ? parsedTextFontSize : 18) * sizeMultiplier;
    const isMobileDevice = checkMobile();
    const menuOuterMargin = isMobileDevice ? 8 * sizeMultiplier : 0;
    const menuContentWidth = storyDimensions.containerWidth - 2 * menuOuterMargin;
    const menuContentHeight = storyDimensions.containerHeight - 2 * menuOuterMargin;
    const targetedElement = useRef(null);

    const [scrollPosition, setScrollPosition] = useState(0);

    const [toolTipOpen, setToolTipOpen] = useState(null);
    const [showPreviewOptimInfo, setShowPreviewOptimInfo] = useState(false);
    const [pageTransitionDirection, setPageTransitionDirection] = useState(0);
    const [isPageTransitioning, setIsPageTransitioning] = useState(false);
    const previousPageNumberRef = useRef(0);
    const pageTransitionUnlockTimeoutRef = useRef(null);

    const transformComponentRef = useRef(null);
    const preloadedPageTypeImgRef = useRef(new Set());
    const [loadedPageTypeImgs, setLoadedPageTypeImgs] = useState({});
    const isMountedRef = useRef(true);
    const menuIdRef = useRef(props.story.menuId);

    useEffect(() => {
        menuIdRef.current = props.story.menuId;
    }, [props.story.menuId]);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    function preloadPageTypeImg(url){
        if (!url || preloadedPageTypeImgRef.current.has(url)) return;
        preloadedPageTypeImgRef.current.add(url);
        try {
            const img = new window.Image();
            img.decoding = "async";
            img.onload = () => {
                if (isMountedRef.current){
                    setLoadedPageTypeImgs((prev) => ({ ...prev, [url]: true }));
                }
            };
            img.src = url;
        }
        catch (error){
            // Nothing
        }
    }

    useEffect(() => {  
        if (pageEntry == null && scrollPosition !== 0){
            let menuContainer = document.getElementById("menu-container");
            if (menuContainer != null){
                menuContainer.scrollTo(0, scrollPosition);
                setScrollPosition(0);
            }
        }

    }, [scrollPosition, pageEntry]);   
    
    useEffect(() => {
        if (pageEntry == null && !isPageTransitioning){
            let menuContainer = document.getElementById("menu-container");
            if (menuContainer != null){
                menuContainer.scrollTo(0, 0);
                setScrollPosition(0);
            }
        }
    }, [pageNumber, pageEntry, isPageTransitioning]);

    useEffect(() => {
        if (pageNumber !== previousPageNumberRef.current){
            setPageTransitionDirection(pageNumber > previousPageNumberRef.current ? 1 : -1);
            previousPageNumberRef.current = pageNumber;
        }
    }, [pageNumber]);

    const minSwipeDistance = 80; 
    const prefersReducedMotion = useReducedMotion();
    const pageRevealDuration = 0.2;
    const menuOpenFadeDuration = prefersReducedMotion ? 0 : 0.35;

    useEffect(() => {
        return () => {
            if (pageTransitionUnlockTimeoutRef.current){
                clearTimeout(pageTransitionUnlockTimeoutRef.current);
            }
        };
    }, []);

    function requestPageChange(delta){
        if (!menuPages || !menuPages.length || isPageTransitioning){
            return false;
        }

        const maxPageIndex = menuPages.length - 1;
        const nextPage = Math.min(Math.max(pageNumber + delta, 0), maxPageIndex);
        if (nextPage === pageNumber){
            return false;
        }

        setIsPageTransitioning(true);
        if (pageTransitionUnlockTimeoutRef.current){
            clearTimeout(pageTransitionUnlockTimeoutRef.current);
        }
        const lockDurationMs = prefersReducedMotion ? 80 : Math.max(140, Math.round(pageRevealDuration * 1000) + 40);
        pageTransitionUnlockTimeoutRef.current = setTimeout(() => {
            setIsPageTransitioning(false);
            pageTransitionUnlockTimeoutRef.current = null;
        }, lockDurationMs);

        setPageNumber(nextPage);
        return true;
    }

    const onTouchStart = (e) => {        
        setTouchEnd(null);
        setTouchEndY(null);
        setTouchStart(e.targetTouches[0].clientX);
        setTouchStartY(e.targetTouches[0].clientY);
    }
    const onTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
        setTouchEndY(e.targetTouches[0].clientY);
    };
    const onTouchEnd = (e) => {
        if (!touchStart || !touchEnd || !menuPages){
            setTouchStart(null);
            setTouchEnd(null);
            setTouchStartY(null);
            setTouchEndY(null);
            return;
        } 
        const deltaX = touchStart - touchEnd;
        const deltaY = touchStartY != null && touchEndY != null ? touchStartY - touchEndY : 0;
        // Mobile gesture guard: only turn page when intent is clearly horizontal.
        const horizontalGesture = Math.abs(deltaX) > Math.abs(deltaY) * 1.2;
        const distance = deltaX;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;
        if (horizontalGesture && isLeftSwipe) {
            if (requestPageChange(1)){
                analyticsManager.logSwipeFullMenuTag(props.storyId, playMode);
                if (getStoredPixelConsent() === 'accepted') {
                    if (window.ttq){
                        window.ttq.track('ViewContent', {                
                            "contents": [
                                {              
                                    "content_id": "0",           
                                    "content_name": "change menu page for " + (pageNumber + 1),
                                }
                            ],
                        });
                    }      
                    if (window.fbq){                    
                        window.fbq('track', 'ViewContent', {"content_name": "change menu page for " + (pageNumber + 1)});
                    }
                }         
            }
        }
        else if (horizontalGesture && isRightSwipe){
            if (requestPageChange(-1)){
                analyticsManager.logSwipeFullMenuTag(props.storyId, playMode);
                if (getStoredPixelConsent() === 'accepted') {
                    if (window.ttq){
                        window.ttq.track('ViewContent', {                
                            "contents": [
                                {              
                                    "content_id": "0",           
                                    "content_name": "change menu page for " + (pageNumber - 1),
                                }
                            ],
                        });
                    }
                    if (window.fbq){                    
                        window.fbq('track', 'ViewContent', {"content_name": "change menu page for " + (pageNumber - 1)});
                    }
                }
            }
        }
        setTouchStart(null);
        setTouchEnd(null);
        setTouchStartY(null);
        setTouchEndY(null);
    };    

    // TODO mutualize
    // disable swipe to close on facebook browser on iOS
    let firstClientX, clientX;
    let firstClientY, clientY;
    const preventTouch = (e) => {        
        clientX = e.touches[0].clientX - firstClientX;
        clientY = e.touches[0].clientY - firstClientY;

        if (Math.abs(clientX) > Math.abs(clientY)) {
            e.preventDefault();
            e.returnValue = false;

            return false;
        }
    };
    const touchStartFB = (e) => {
        firstClientX = e.touches[0].clientX;
        firstClientY = e.touches[0].clientY;
    };
    const containerRef = useRef(window);
    useEffect(() => {
        if (containerRef.current === null || ! (isFacebookInAppBrowser() && isIOS())) {
            return;
        }
        if (! props.show) {
            try {
                containerRef.current.removeEventListener('touchstart', touchStartFB);
                containerRef.current.removeEventListener('touchmove', preventTouch, { passive: false });
            } catch (e) {}
        }

        containerRef.current.addEventListener('touchstart', touchStartFB);
        containerRef.current.addEventListener('touchmove', preventTouch, { passive: false });
    }, [containerRef, props.show]);

    useEffect(() => {
        setMenu(null);
        setMenuPages(null);
        setPageNumber(0);
        if (props.story.menuId != null) {
            getMenuPages(props.story.menuId, 0);
        } else if ((playMode === "preview" || playMode === "demo" || isCafeUprealityDemoStoryId(props.storyId)) && menuPages == null) {
            let demoPages = playMode === "preview" ? getMakerDemoProductPages() : getDemoProductPages();
            if (playMode === "preview") {
                setShowPreviewOptimInfo(true);
            }
            setMenuPages(demoPages);
            setMenu({ menuPages: demoPages });
            demoPages.forEach((page) => preloadPageTypeImg(page && page.pageTypeImg ? page.pageTypeImg : null));
        }
    }, [props.story.menuId]);

    useEffect(() => {
        if (props.story.menuId == null || pageNumber === 0) {
            return;
        }
        getMenuPages(props.story.menuId, pageNumber);
      }, [props.story.menuId, pageNumber]);

      useEffect(() => {
        if (props.editorPages && props.editorPages.length > 0){
            setMenuPages(props.editorPages);
            const menu = { ...props.editorMenu };
            menu.menuPages = props.editorPages;            
            setMenu(menu);
            if (menu.menuPages[pageNumber] && menu.menuPages[pageNumber].pageEntries && !menuHasAllergens)     
                setMenuHasAllergens(hasAllergens(menu.menuPages[pageNumber].pageEntries));
        }
      }, [props.editorPages, props.editorMenu]);

    useEffect(() => {
        if (!menuPages || !menuPages[pageNumber]) return;
        preloadPageTypeImg(menuPages[pageNumber].pageTypeImg);
        if (menuPages[pageNumber + 1]){
            preloadPageTypeImg(menuPages[pageNumber + 1].pageTypeImg);
        }
        if (menuPages[pageNumber + 2]){
            preloadPageTypeImg(menuPages[pageNumber + 2].pageTypeImg);
        }
    }, [menuPages, pageNumber]);

    async function getMenuPages(menuId, pageNumber){
        const requestMenuId = menuId;
        let localeMenu = menu;     
        if (!menu){
            const db = getFirestore();
            const menuRef = doc(db, "menus", menuId);
            let menuDoc = await getDoc(menuRef).catch((error) => {                
                Sentry.captureMessage("menu initial fetch failed, retrying " + printObj(error));
            });
            if (!menuDoc){                
                menuDoc = await getDoc(menuRef).catch((error) => {                
                    Sentry.captureMessage("menu retry failed " + printObj(error));
                });
            }
            if (menuDoc) {
                localeMenu = menuDoc.data();
            }
            if (requestMenuId !== menuIdRef.current) {
                return;
            }
            setMenu(localeMenu);
        }
              
        const localMenuPages = menuPages == null ? [] : [...menuPages];
        if (pageNumber === 0 && localeMenu && localeMenu.menuPages && localeMenu.menuPages.length > 0 && localMenuPages.length === 0){
            let menuPage = await getMenuPage(localeMenu.menuPages[0]);
            if (!menuPage){
                menuPage = await getMenuPage(localeMenu.menuPages[0]);
            }
            localMenuPages.push(menuPage);
        }
        if (localeMenu && localeMenu.menuPages){
            while (localMenuPages.length < pageNumber + 3 && localeMenu.menuPages.length > localMenuPages.length){
                const nextPageId = localeMenu.menuPages[localMenuPages.length];
                let menuPage = await getMenuPage(nextPageId);
                if (!menuPage){
                    menuPage = await getMenuPage(nextPageId);
                }
                if (!menuPage) break;
                localMenuPages.push(menuPage);
                preloadPageTypeImg(menuPage.pageTypeImg);
            }
        }
        
        if (requestMenuId !== menuIdRef.current) {
            return;
        }

        setMenuPages(localMenuPages);
        if (localMenuPages && localMenuPages[pageNumber] && localMenuPages[pageNumber].pageEntries &&!menuHasAllergens)
            setMenuHasAllergens(hasAllergens(localMenuPages[pageNumber].pageEntries));
    }

    async function getMenuPage(menuPageId){
        const db = getFirestore();
        const menuPageRef = doc(db, "menu_pages", menuPageId);
        let menuPage = await getDoc(menuPageRef).catch((error) => {                
            Sentry.captureMessage("menu page fetch failed " + printObj(error));
        });
        if (!menuPage){                
            menuPage = await getDoc(menuPageRef).catch((error) => {                
                Sentry.captureMessage("menu page retry failed " + printObj(error));
            });
        }
        
        if (!menuPage){
            return null;
        }
        return menuPage.data();
    }

    let marginBtwTopTitle = 0;
    if (props.story.menuStyle !== "modern"){
        marginBtwTopTitle = (props.story.menuStyle !== "custom" ? 36 : 28) * sizeMultiplier;
    }
    else if (props.story.menuStyle === "modern"){
        marginBtwTopTitle = 52 * sizeMultiplier;
    }

    const textColorStyle = props.story.menuStyle === "custom" && menu && menu.textColor ? {color: menu.textColor} : {};    

    const title = () => {     
        if (!menuPages || !menuPages[pageNumber]) return null;
        
        let titleStyle: CSSProperties = {};
        let imgSrc = "";
        let imgStyle: CSSProperties = {};       
        let titleSuffix = "";
        let textWidthAddition = 0;
        
        let pageTypeText = "";
        if (menuPages[pageNumber].pageType != null && menuPages[pageNumber].pageType.fr){            
            pageTypeText = getLocalizedMenuText(menuPages[pageNumber].pageType);                        
        }

        if (pageTypeText && pageTypeText.length < 8)
            textWidthAddition = 30;
        
        switch (props.story.menuStyle){
            case "elegant":
                titleStyle = {color: '#404040', fontSize: 44 * sizeMultiplier, fontFamily: 'Alex brush'};
                imgSrc = "/images/separatorElegant.png";
                // TODO make it proportional to text length
                imgStyle = {marginTop: 10 * sizeMultiplier, width: 120 * sizeMultiplier, height:8 * sizeMultiplier};
                break;
            case "friendly":
                titleStyle = {color: '#404040', fontSize: 36 * sizeMultiplier, fontFamily: 'Roboto condensed', fontWeight: 'bold'};
                imgSrc = "/images/separatorFriendly.png";
                titleSuffix = " " + getEmojiFromPageType(menuPages[pageNumber].pageType.en);
                imgStyle = {marginTop: 10 * sizeMultiplier, width: 100 * sizeMultiplier, height:16 * sizeMultiplier};                
                break;
            case "modern":
                titleStyle = {color: '#404040', fontSize: 36 * sizeMultiplier, fontFamily: 'Roboto condensed', fontWeight: 'bold'};
                imgSrc = "";
                imgStyle = {};
                break;
            case "simple":                
                titleStyle = {color: '#404040', fontSize: 36 * sizeMultiplier, fontFamily: titleFont ? titleFont : (menuTitleFontFallback ? 'Roboto, sans-serif' : 'Roboto')};
                imgSrc = "/images/separatorSimple.png";
                imgStyle = {marginTop: 10 * sizeMultiplier, width: getTextWidth(pageTypeText, 36 * sizeMultiplier + "px " + (titleFont ? titleFont : "Roboto")), height:3 * sizeMultiplier};
                break;
            case "custom":
                titleStyle = {...titleColorStyle, fontSize: (menu.titleFontSize ? menu.titleFontSize : 36) * sizeMultiplier * titleFontSizeMult, fontFamily: titleFont ? titleFont : (menuTitleFontFallback ? 'Roboto, sans-serif' : 'Roboto'), ...titleFontBoldStyle};
                imgSrc = "";
                imgStyle = {};
                break;
        }

        if (props.story.menuStyle !== "modern" && props.story.menuStyle !== "custom"){                       
            return (
                <div className="menu-page-title-container" style={{marginTop: marginBtwTopTitle}}>
                    <div style={{...titleStyle, ...textColorStyle, userSelect: 'none', WebkitUserSelect: 'none'}}>{pageTypeText}{titleSuffix}</div>
                    {pageTypeText && <Image source={imgSrc} style={{...imgStyle}} tintColor={menuColor}/>}
                </div> 
            );
        }
        else if (props.story.menuStyle !== "custom"){               
            const titleSizeRoboto = getTextWidth(pageTypeText + textWidthAddition, 36 * sizeMultiplier + "px Roboto condensed");      
            const separatorLeftX = 0;
            const separatorRightX = separatorLeftX + titleSizeRoboto + (27 - 92 + 27) * sizeMultiplier;            
            
            return (
                <div className='menu-page-title-container' style={{marginTop: marginBtwTopTitle, }}>
                    <div style={{height: pageTypeText ? 66 * sizeMultiplier : 0,}}/>
                    <div style={{position: 'absolute', width: titleSizeRoboto + 2 * 27 * sizeMultiplier, height: 66 * sizeMultiplier}}>
                        <div className='menu-page-title' style={{...titleStyle, position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', whiteSpace: 'nowrap', userSelect: 'none', WebkitUserSelect: 'none', ...textColorStyle}}>{pageTypeText}</div>
                        {
                            pageTypeText && 
                                <React.Fragment key="separators">
                                    <Image key="separator-left" source="/images/separatorModernLeftLittle.png" style={{left: separatorLeftX, height: 66 * sizeMultiplier, width: 92 * sizeMultiplier, position:'absolute', top: '50%', transform: 'translate(0%, -50%)'}} tintColor={menuColor}/>
                                    <Image key="separator-right" source="/images/separatorModernRightLittle.png" style={{left: separatorRightX, height: 66 * sizeMultiplier, width: 92 * sizeMultiplier, position:'absolute', top: '50%', transform: 'translate(0%, -50%)'}} tintColor={menuColor}/>                            
                                </React.Fragment>
                        }
                    </div> 
                </div>                
            );
        }
        else {
            const pageTypeImgSize = (menu && menu.titleFontSize ? menu.titleFontSize : 36) * sizeMultiplier * titleFontSizeMult * 0.9;
            const pageTypeImgSrc = menuPages[pageNumber].pageTypeImg;
            const pageTypeImgLoaded = pageTypeImgSrc ? loadedPageTypeImgs[pageTypeImgSrc] === true : false;
            return (
                <div className="menu-page-title-container" style={{marginTop: marginBtwTopTitle}}>
                    <div style={{...titleStyle, display: 'flex', flexDirection: 'row',  alignItems: 'center', paddingLeft: 24 * sizeMultiplier, paddingRight: 24 * sizeMultiplier, textAlign: 'center', userSelect: 'none', WebkitUserSelect: 'none' }}>
                        {pageTypeText}
                        {pageTypeImgSrc && (
                            <span key={`page-type-img-${pageTypeImgSrc}`} style={{position: 'relative', marginLeft: 8, width: pageTypeImgSize, height: pageTypeImgSize, display: 'inline-block'}}>
                                {!pageTypeImgLoaded && (
                                    <span style={{position: 'absolute', inset: 0, borderRadius: 6 * sizeMultiplier, backgroundColor: addOpacityToHexColor(menuColor, 0.2)}} />
                                )}
                                {pageTypeImgLoaded ? (
                                    <img
                                        key={pageTypeImgSrc}
                                        src={pageTypeImgSrc}
                                        alt={pageTypeText}
                                        draggable={false}
                                        style={{width: pageTypeImgSize, height: pageTypeImgSize, opacity: 1, transition: 'opacity 120ms ease-out', position: 'relative', zIndex: 1, userSelect: 'none', WebkitUserSelect: 'none', WebkitUserDrag: 'none'}}
                                    />
                                ) : (
                                    <img
                                        key={pageTypeImgSrc}
                                        src={pageTypeImgSrc}
                                        alt={pageTypeText}
                                        draggable={false}
                                        onLoad={() => setLoadedPageTypeImgs((prev) => ({ ...prev, [pageTypeImgSrc]: true }))}
                                        style={{width: pageTypeImgSize, height: pageTypeImgSize, opacity: 0, position: 'relative', zIndex: 1, pointerEvents: 'none', userSelect: 'none', WebkitUserSelect: 'none', WebkitUserDrag: 'none'}}
                                    />
                                )}
                            </span>
                        )}
                    </div>                    
                </div> 
            );
        }
    };

    function hasAllergens(pageEntries){
        let hasAllergens = false;
        for (const pageEntry of pageEntries){            
            if (pageEntry.entries){                
                for (const entry of pageEntry.entries){
                    if (entry.allergens){
                        hasAllergens = true;
                        break;
                    }
                }
            }            
        }
        return hasAllergens;
    }

    function hasNoSelectedAllergens(entry){                
        let showEntry = true;
        if (entry.allergens){                        
            const allergensTable = filteredAllergens.split(",");
            for (const allergen of allergensTable){
                if (allergen !== "" && entry.allergens.toLowerCase().includes(allergen.trim().toLowerCase())){
                    showEntry = false;
                    break;
                }
            }
        }

        return showEntry;
    }

    function getEntryTitleInfo(entry){
        const localizedTitle = getLocalizedMenuText(entry?.entryTitle);
        const hasVegetarianMarker = /\s*\(\s*v\s*\)\s*/i.test(localizedTitle);
        const cleanTitle = localizedTitle.replace(/\s*\(\s*v\s*\)\s*/gi, " ").replace(/\s{2,}/g, " ").trim();
        return {
            isVegetarian: hasVegetarianMarker,
            cleanTitle: cleanTitle,
        };
    }

    function getVegetarianLabel(){
        return t('MenuVegetarianLabel');
    }

    function getSignatureLabel(){
        return t('MenuSignatureLabel');
    }

    function isDishListPageEntry(pEntry) {
        return pEntry && (pEntry.entryType === "dishList" || pEntry.entryType?.fr);
    }

    function getEntriesTitleText(pEntry) {
        if (pEntry.entriesTitle == null) return "";
        return getLocalizedMenuText(pEntry.entriesTitle).trim();
    }

    function isNonEmptySymbolicOnlyEntriesTitle(pEntry) {
        if (pEntry.entriesTitle == null) return false;
        const text = getLocalizedMenuText(pEntry.entriesTitle).trim();
        return text.length > 0 && !/\p{L}/u.test(text);
    }
    
    let hideDescriptionPrice = false;
    if (props.storyId === "LnSX2qKrthKsRmr1vcxc" || props.storyId === "PaBK2iq1At5um1V6nNGo" || props.storyId === "1iDXY4hOyo1PVyrvCmvG" || props.storyId === "4WlhflMBRftrhBabhzqL" || props.storyId === "G4uGdmkDklxxMumN9FIc" || props.storyId === "LQ8cYZF1BrqCbKYOI7g7" || props.storyId === "P4hss0ADGtE2wMY5fcrQ" || props.storyId === "YPuRVqGYO2Q2QFKEkRvQ" || props.storyId === "ZcJ9o5kOjzeIvIZ4z0kw" || props.storyId === "clexvJMGmwlGXjY8KLaT" || props.storyId === "nRyYRVND1xh34m32eGMa" || props.storyId === "rj1ZbTjACKeCMOqqAi4l" || props.storyId === "wfnhdxPJPy3v54dXJAr4" || props.storyId === "xlqSrGF4RJz9yfV2MQrr" || props.storyId === "zUEpyuVwo5Tws3hnvFds"){
        hideDescriptionPrice = true;
    }

    const disableDetailWithImg = menu?.disableDetailWithImg === true;
    const entryImageDimensions = 72 * sizeMultiplier;
    let listItems = [];
    let allergensAlreadyDisplayed = false;
    if (menuPages != null && menuPages[pageNumber]){ 
        if (menuPages[pageNumber] && menuPages[pageNumber].pageType && menuPages[pageNumber].pageType.fr === "image"){
            listItems[0] = <React.Fragment key={`image-page-${pageNumber}`}>
                    <div
                        className="FullScreenContainer"
                        style={{
                            backgroundColor:
                              menuPages[pageNumber].pageBackground
                              || (props.story.menuStyle === "custom" && menu && menu.backgroundColor)
                              || "#FFFFFF",
                            top: 0,
                            left: 0,
                            width: menuContentWidth,
                            height: menuContentHeight,
                        }}
                    >
                        <TransformWrapper ref={transformComponentRef}>
                            <TransformComponent>
                                <img alt={t('FullScreenMenuPageAlt')} src={menuPages[pageNumber].pageImgUrl} draggable={false} style={{objectFit: "contain", width: menuContentWidth, height: menuContentHeight, userSelect: 'none', WebkitUserSelect: 'none', WebkitUserDrag: 'none'}}/>
                            </TransformComponent>
                        </TransformWrapper>                              
                    </div>
                </React.Fragment>;
        }
        else if (isRichTextMenuPage(menuPages[pageNumber])) {
            const richPage = menuPages[pageNumber];
            const richBg =
                richPage.pageBackground
                || (props.story.menuStyle === "custom" && menu && menu.backgroundColor)
                || "#FFFFFF";
            const rawImg = richPage.pageImgUrl;
            const richImgUrl = String(Array.isArray(rawImg) ? rawImg[0] : rawImg || "").trim();
            const imgPosition = String(richPage.pageImgPosition || "top").trim().toLowerCase() === "bottom"
                ? "bottom"
                : "top";
            const photoHeight = Math.min(
                Math.round(menuContentWidth * 9 / 16),
                Math.round(menuContentHeight * 0.42)
            );
            const richHtml = sanitizeMenuRichTextHtml(getLocalizedMenuText(richPage.pageHtml));
            const photoEl = richImgUrl ? (
                <div
                    style={{
                        width: menuContentWidth,
                        height: photoHeight,
                        flexShrink: 0,
                        overflow: "hidden",
                        backgroundColor: "#000",
                    }}
                >
                    <img
                        alt=""
                        src={richImgUrl}
                        draggable={false}
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            objectPosition: "center center",
                            display: "block",
                            userSelect: "none",
                            WebkitUserSelect: "none",
                            WebkitUserDrag: "none",
                        }}
                    />
                </div>
            ) : null;
            const textEl = (
                <div
                    style={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: "auto",
                        WebkitOverflowScrolling: "touch",
                        padding: `${16 * sizeMultiplier}px ${24 * sizeMultiplier}px ${60 * sizeMultiplier}px`,
                        boxSizing: "border-box",
                        fontSize: 15 * sizeMultiplier,
                        lineHeight: 1.45,
                        color: "#404040",
                        textAlign: "start",
                        userSelect: "none",
                        WebkitUserSelect: "none",
                        ...textFontStyle,
                        ...textColorStyle,
                    }}
                    dangerouslySetInnerHTML={{ __html: richHtml || "" }}
                />
            );
            listItems[0] = (
                <React.Fragment key={`richtext-page-${pageNumber}`}>
                    <div
                        style={{
                            backgroundColor: richBg,
                            width: menuContentWidth,
                            minHeight: menuContentHeight,
                            height: menuContentHeight,
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                        }}
                    >
                        {imgPosition === "top" ? photoEl : null}
                        {textEl}
                        {imgPosition === "bottom" ? photoEl : null}
                    </div>
                </React.Fragment>
            );
        }
        else {
            const dishListSkipLastEntrySeparator = {};
            const dishListLastEntrySeparatorSpacerOnly = {};
            const pageEntriesForPage = menuPages[pageNumber].pageEntries;
            if (pageEntriesForPage) {
                let dishListOrdinal = 0;
                let lastDishListPageEntryIndex = null;
                for (let i = 0; i < pageEntriesForPage.length; i++) {
                    const pEntry = pageEntriesForPage[i];
                    if (!isDishListPageEntry(pEntry)) continue;
                    if (dishListOrdinal >= 1 && lastDishListPageEntryIndex != null && isNonEmptySymbolicOnlyEntriesTitle(pEntry)) {
                        dishListSkipLastEntrySeparator[lastDishListPageEntryIndex] = true;
                    }
                    lastDishListPageEntryIndex = i;
                    dishListOrdinal++;
                }
                if (lastDishListPageEntryIndex != null) {
                    dishListLastEntrySeparatorSpacerOnly[lastDishListPageEntryIndex] = true;
                }
            }

            listItems = menuPages[pageNumber].pageEntries && menuPages[pageNumber].pageEntries.map((pageEntry, index) =>{                         
                
                if (pageEntry.entryType === "startingNote" || pageEntry.entryType === "endingNote"){  
                    const textColumnWidth = menuContentWidth - 48 * sizeMultiplier;
                    const noteFontFamily = pageEntry.entryType === "endingNote"
                        ? (resolvedDescFontFamily ? resolvedDescFontFamily : "Roboto")
                        : (resolvedTextFontFamily ? resolvedTextFontFamily : "Roboto");
                    const noteOn2Lines = getTextWidth(getLocalizedMenuText(pageEntry.text), "" + 14 * sizeMultiplier + "px " + noteFontFamily) > menuContentWidth - 2 * 24 * sizeMultiplier;

                    let marginTop = 10 * sizeMultiplier;
                    let marginBottom = 10 * sizeMultiplier;
                    if (pageEntry.entryType === "startingNote"){ 
                        if (props.story.menuStyle === "modern"){
                            marginTop = - 10 * sizeMultiplier;
                            marginBottom = 30 * sizeMultiplier;
                        }
                        else  {
                            marginTop = - 36 * sizeMultiplier;
                            marginBottom = 30 * sizeMultiplier;
                        }
                    }            

                    return (
                        <div key={`${pageNumber}-note-${index}-${pageEntry.entryType}`} style={{width: textColumnWidth, textAlign: noteOn2Lines ? 'start' : 'center', fontSize: 14 * sizeMultiplier, fontStyle: 'italic', color: '#707070', marginTop: marginTop, marginBottom: marginBottom, userSelect: 'none', WebkitUserSelect: 'none', ...(pageEntry.entryType === "endingNote" ? descFontStyle : textFontStyle), ...(pageEntry.entryType === "endingNote" ? descColorStyle : textColorStyle)}}>
                             <span style={pageEntry.entryType === "endingNote" ? descFontStyle : textFontStyle} dangerouslySetInnerHTML={{__html: getLocalizedMenuText(pageEntry.text)?.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '').replace(/\n/g, '<br>').replace(/<[^>]*>/g, (match) => {
                                             // Autoriser seulement les balises <b>, <i>, <u>, <strong>, <em>, <br>, <img>
                                             const allowedTags = ['b', 'i', 'u', 'strong', 'em', 'br', 'img'];
                                             const tagName = match.replace(/<\/?([^>\s]+).*/, '$1').toLowerCase();
                                             
                                             if (allowedTags.includes(tagName)) {
                                                 // Pour les balises img, valider les attributs autorisés
                                                 if (tagName === 'img') {
                                                    const sanitizedImg = match.replace(/<img([^>]*)>/gi, (fullMatch, attributes) => {
                                                        // Extraire et valider les attributs src, alt et height
                                                        const srcMatch = attributes.match(/src\s*=\s*["']([^"']*)["']/i);
                                                        const altMatch = attributes.match(/alt\s*=\s*["']([^"']*)["']/i);
                                                        const heightMatch = attributes.match(/height\s*=\s*["']([^"']*)["']/i);
                                                        
                                                        let sanitizedAttrs = '';
                                                        if (srcMatch && srcMatch[1]) {
                                                            // Valider que l'URL est sécurisée (http, https, ou chemin relatif)
                                                            const src = srcMatch[1];
                                                            if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('/') || src.startsWith('./')) {
                                                                sanitizedAttrs += ` src="${src}"`;
                                                            }
                                                        }
                                                        if (altMatch && altMatch[1]) {
                                                            sanitizedAttrs += ` alt="${altMatch[1]}"`;
                                                        }
                                                        if (heightMatch && heightMatch[1]) {
                                                            // Valider que la hauteur est un nombre ou un pourcentage
                                                            const height = heightMatch[1];
                                                            if (/^\d+(px|%|em|rem)?$/.test(height) || /^\d+$/.test(height)) {
                                                                sanitizedAttrs += ` height="${height}"`;
                                                            }
                                                        }
                                                        
                                                        return `<img${sanitizedAttrs}>`;
                                                    });
                                                     return sanitizedImg;
                                                 }
                                                 return match;
                                             }
                                             return '';
                                         })}} />
                        </div>
                    ); 
                }    
                else if (pageEntry.entryType === "dishList" || pageEntry.entryType.fr){
                    const startingNoteOn2Lines = pageEntry.startingNote && getTextWidth(getLocalizedMenuText(pageEntry.startingNote), "" + 14 * sizeMultiplier + "px " + (resolvedTextFontFamily ? resolvedTextFontFamily : "Roboto")) > menuContentWidth - 2 * 24 * sizeMultiplier;
                    const endingNoteOn2Lines = pageEntry.endingNote && getTextWidth(getLocalizedMenuText(pageEntry.endingNote), "" + 14 * sizeMultiplier + "px " + (resolvedDescFontFamily ? resolvedDescFontFamily : "Roboto")) > menuContentWidth - 2 * 24 * sizeMultiplier;

                    const startingNoteCentering = startingNoteOn2Lines ? 'start' : 'center';
                    const endingNoteCentering = endingNoteOn2Lines ? 'start' : 'center';
                    
                    const notFirstPageEntry = menuPages[pageNumber].pageEntries.findIndex((pEntry)=>pEntry.entriesTitle === pageEntry.entriesTitle) > 0;
                    const skipLastEntrySeparator = dishListSkipLastEntrySeparator[index] === true;
                    const lastEntrySeparatorSpacerOnly = dishListLastEntrySeparatorSpacerOnly[index] === true;
                    const symbolicOnlyEntriesTitle = isNonEmptySymbolicOnlyEntriesTitle(pageEntry);
                    const entriesTitleFontSize = symbolicOnlyEntriesTitle ? (18) * sizeMultiplier : 24 * sizeMultiplier;

                    let showAllergensSelector = ! allergensAlreadyDisplayed;
                    allergensAlreadyDisplayed = true;                

                    if (pageEntry.entries){                           
                        return (
                            <div key={`${pageNumber}-entries-${index}`}>
                                {menuHasAllergens && showAllergensSelector &&
                                    <div style={{width: "100%", display: 'flex', display: menuPages[pageNumber].showAllergensFilter ? '' : 'none', flexDirection: 'row', justifyContent: 'center', marginBottom: 20}}>
                                        <AllergensSelect initialValue={filteredAllergens.split(",")} color={props.story.primaryColor} callback={(values)=> setFilteredAllergens(values)}/>
                                    </div>
                                } 
                                {pageEntry.entriesTitle != null && <div style={{marginTop: notFirstPageEntry ? 10 * sizeMultiplier : 0, marginBottom: 20 * sizeMultiplier, fontSize: entriesTitleFontSize, color: '#404040', textAlign: 'center', ...textFontStyle, ...textColorStyle, ...(symbolicOnlyEntriesTitle || !categoryFontStyleLikeTitle ? {} : titleFontStyle)}}>{getLocalizedMenuText(pageEntry.entriesTitle)}</div>}
                                {pageEntry.startingNote != null && 
                                    <div style={{width: menuContentWidth - 48 * sizeMultiplier, textAlign: startingNoteCentering, fontSize: 14 * sizeMultiplier, fontStyle: 'italic', color: '#707070', marginBottom: 20 * sizeMultiplier, ...textFontStyle, ...textColorStyle}}>
                                        {getLocalizedMenuText(pageEntry.startingNote)}
                                    </div>
                                }                                               
                                {pageEntry.entries && pageEntry.entries.map((entry) => {
                                        const textColumnWidth = menuContentWidth - 48 * sizeMultiplier - (entry.entryImage != null ? (entryImageDimensions + 12 * sizeMultiplier) : 0);                       

                                        function openMealDetail(){
                                            if (disableDetailWithImg) {
                                                return;
                                            }
                                            let menuContainer = document.getElementById("menu-container");
                                            if (menuContainer != null){
                                                setScrollPosition(menuContainer.scrollTop);
                                                analyticsManager.logOpenDishDetalTag(props.storyId, playMode, true);
                                                if (getStoredPixelConsent() === 'accepted') {
                                                    if (window.ttq){
                                                        window.ttq.track('ViewContent', {                
                                                            "contents": [
                                                                {              
                                                                    "content_id": "0",           
                                                                    "content_name": "open menu detail for " + getLocalizedMenuText(entry.entryTitle),
                                                                }
                                                            ],
                                                        });
                                                    }
                                                    if (window.fbq){                    
                                                        window.fbq('track', 'ViewContent', {"content_name": "open menu detail for " + getLocalizedMenuText(entry.entryTitle)});
                                                    }
                                                }
                                                setPageEntry(entry);
                                            }
                                        }

                                        const img = () => {
                                            if (entry.entryImage != null){
                                                return (                                            
                                                    <img src={entry.entryImage} draggable={false} onLoad={(e) => e.target.style.color='white'} onError={(e) => e.target.style.color='transparent'} style={{marginLeft: 12 * sizeMultiplier, width: entryImageDimensions, height: entryImageDimensions, objectFit: 'cover', borderRadius: 20, pointerEvents: 'none', userSelect: 'none', WebkitUserSelect: 'none', WebkitUserDrag: 'none'}} alt={isFirefox() ? "":"thumbnail of a dish"} />
                                                );
                                            }
                                            else return null;
                                        };                              

                                        const getLastShownEntryId = () => {
                                            let lastId = null;
                                            for (const e of pageEntry.entries) {
                                                if (hasNoSelectedAllergens(e) && e.entryTitle != null) {
                                                    lastId = e.entryId;
                                                }
                                            }
                                            return lastId;
                                        };

                                        const separatorMarginsStyle = {
                                            marginTop: 20 * sizeMultiplier,
                                            marginBottom: 20 * sizeMultiplier,
                                            width: 180 * sizeMultiplier,
                                            height: 1,
                                        };

                                        const separator = () => {
                                            const lastShownEntryId = getLastShownEntryId();
                                            if (entry.entryId === lastShownEntryId) {
                                                if (lastEntrySeparatorSpacerOnly) {
                                                    return <div aria-hidden style={separatorMarginsStyle} />;
                                                }
                                                if (skipLastEntrySeparator) {
                                                    return null;
                                                }
                                            }
                                            if (entry.entryId !== menuPages[pageNumber].pageEntries[menuPages[pageNumber].pageEntries.length -1].entryId){
                                                return (
                                                    <div
                                                        aria-label={t('LineSeparatorAlt')}
                                                        style={{
                                                            ...separatorMarginsStyle,
                                                            backgroundColor: addOpacityToHexColor(menuColor, 0.2),
                                                            borderRadius: 999,
                                                        }}
                                                    />
                                                );
                                            }
                                            else return null;            
                                        };   
                                        
                                        const showEntry = hasNoSelectedAllergens(entry);

                                        if (showEntry){                                        
                                            const entryTitleInfo = getEntryTitleInfo(entry);
                                            const entryTitle = entryTitleInfo.cleanTitle;
                                            const canOpenDetail = entry.entryImage != null && !disableDetailWithImg;
                                            const canToggleAllergenTooltip = !!(entry.allergens && !canOpenDetail);
                                            // Mobile/demo: les blocs texte interceptent le scroll. pointer-events:none
                                            // sauf si le tooltip allergènes doit rester cliquable.
                                            // (fix 904d298 — étendu aussi aux entrées avec image / canOpenDetail)
                                            const disableTextPointerOnMobile = (checkMobile() || playMode === "demo") && !canToggleAllergenTooltip;
                                            const highlightedEntryStyle = entry.signature === true ? {
                                                borderRadius: 10 * sizeMultiplier,
                                                padding: 8 * sizeMultiplier,
                                                backgroundColor: addOpacityToHexColor("#7a7a7a", 0.08),
                                            } : {};

                                            function onLineTouchStartListener(e){                                            
                                                if (canOpenDetail)
                                                    targetedElement.current = {element: e.target, x: e.touches[0].clientX, y: e.touches[0].clientY};;                                           
                                            }

                                            function onLineTouchMoveListener(e){
                                                if (canOpenDetail){
                                                    targetedElement.current.lastX = e.touches[0].clientX;      
                                                    targetedElement.current.lastY = e.touches[0].clientY; 
                                                    targetedElement.current.hasMove = true;     
                                                }
                                            }
                                        
                                            function onLineTouchEndListener(e){                                            
                                                if(canOpenDetail && targetedElement.current && targetedElement.current.element === e.target && !targetedElement.current.hasMove /*(targetedElement.current.lastX ? Math.abs(targetedElement.current.lastX - targetedElement.current.x) < 50 : true) && (targetedElement.current.lastY ? Math.abs(targetedElement.current.lastY - targetedElement.current.y) < 50 : true)*/){
                                                    openMealDetail();
                                                };      
                                                targetedElement.current = null;
                                            }

                                            return(                   
                                                <div key={pageNumber + "-" + entry.entryId} className='menu-line-sep-container' >
                                                    <div className='menu-line-container' style={highlightedEntryStyle} onTouchStart={canOpenDetail ? (e)=>onLineTouchStartListener(e) : undefined} onTouchMove={canOpenDetail ? (e)=>onLineTouchMoveListener(e) : undefined} onTouchEnd={canOpenDetail ? (e)=>onLineTouchEndListener(e) : undefined} onClick={canOpenDetail ? ()=>openMealDetail() : undefined}>
                                                        <div className='menu-line-container-wo-img' style={{width: textColumnWidth, pointerEvents: disableTextPointerOnMobile ? 'none' : 'auto', /*height: entryImageDimensions*/}} >                                                      
                                                            {entry.signature === true &&
                                                                <div style={{width: textColumnWidth, textAlign: "start", marginBottom: 6 * sizeMultiplier}}>
                                                                    <span style={{display: 'inline-block', padding: `${2 * sizeMultiplier}px ${7 * sizeMultiplier}px`, borderRadius: 999, fontSize: 10 * sizeMultiplier, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: props.story.secondaryColor, backgroundColor: addOpacityToHexColor(menuColor, 0.9), border: `1px solid ${addOpacityToHexColor(props.story.secondaryColor, 0.35)}`, userSelect: 'none', WebkitUserSelect: 'none'}}>
                                                                        {getSignatureLabel()}
                                                                    </span>
                                                                </div>
                                                            }
                                                            <div className="menu-entry-title" style={{width: textColumnWidth, textAlign: "start", fontSize: entryTitleFontSizePx, color: '#404040', ...textFontStyle, ...textColorStyle, ...textFontBoldStyle, pointerEvents: canToggleAllergenTooltip ? 'auto' : (disableTextPointerOnMobile ? 'none' : 'auto')}}>
                                                                {canToggleAllergenTooltip ? (
                                                                    <LightTooltip open={toolTipOpen != null && entry.entryTitle && toolTipOpen === entryTitle} sx={{...textFontStyle, ...textColorStyle}} onClick={()=>setToolTipOpen(toolTipOpen === entryTitle ? null : entryTitle)} title={entry.allergens && entry.allergens.split(',').map((allergen)=>t(allergen.trim())).join(' - ')}>
                                                                        <span style={{...textFontStyle, ...textColorStyle}}>
                                                                            {entryTitle}
                                                                            {entry.allergens && !entry.entryImage && <img src="/images/ic_allergies.svg" alt={t('AllergensLabel')} style={{marginLeft: 8 * sizeMultiplier, height: (18) * sizeMultiplier, width: (18) * sizeMultiplier, display: 'inline', verticalAlign: 'baseline', transform: `translateY(${4 * sizeMultiplier}px)`}} />}
                                                                        </span>
                                                                    </LightTooltip>
                                                                ) : (
                                                                    <span style={{...textFontStyle, ...textColorStyle}}>
                                                                        {entryTitle}
                                                                        {entry.allergens && !entry.entryImage && <img src="/images/ic_allergies.svg" alt={t('AllergensLabel')} style={{marginLeft: 8 * sizeMultiplier, height: (18) * sizeMultiplier, width: (18) * sizeMultiplier, display: 'inline', verticalAlign: 'baseline', transform: `translateY(${4 * sizeMultiplier}px)`}} />}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {entry.entrySubtitle != null && !hideDescriptionPrice && !hideMenuDescription && 
                                                                <div className="menu-entry-subtitle" style={{width: textColumnWidth, /*height: 34 * sizeMultiplier,*/ textAlign: "start", fontSize: 14 * sizeMultiplier, fontStyle: 'italic', color: '#707070', marginTop: 4 * sizeMultiplier, ...descFontStyle, ...descColorStyle, ...(entry.entryImage != null ? {display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, lineClamp: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'normal'} : {whiteSpace: 'pre-line'}), pointerEvents: disableTextPointerOnMobile ? 'none' : 'auto'}}>{getLocalizedMenuText(entry.entrySubtitle)}</div>
                                                            }
                                                            {entryTitleInfo.isVegetarian &&
                                                                <div style={{marginTop: 8 * sizeMultiplier, width: textColumnWidth, textAlign: "start"}}>
                                                                    <span style={{display: 'inline-flex', alignItems: 'center', gap: 6 * sizeMultiplier, fontSize: 12 * sizeMultiplier, fontWeight: 500, color: '#6a6a6a', userSelect: 'none', WebkitUserSelect: 'none'}}>
                                                                        <span style={{width: 6 * sizeMultiplier, height: 6 * sizeMultiplier, borderRadius: '50%', backgroundColor: '#8a8a8a'}} />
                                                                        {getVegetarianLabel()}
                                                                    </span>
                                                                </div>
                                                            }
                                                            {entry.wineLabels && 
                                                                <div style={{width: textColumnWidth, textAlign: 'end', marginTop: 4 * sizeMultiplier, }}>
                                                                    {    
                                                                    entry.wineLabels.map((label) => {
                                                                         const labelImage = {
                                                                             "bio": "/images/labels-vin/Vin-bio.png",
                                                                            "organic": "/images/labels-vin/1200px-Organic-Logo.svg_.png",
                                                                            "hve": "/images/labels-vin/135-certification-hve.png",
                                                                            "vigneron-independant": "/images/labels-vin/logo-vigneron-independant.jpg",
                                                                            "terra-vitis": "/images/labels-vin/terra-vitis.png",         
                                                                            "nature-progres": "/images/labels-vin/Logo_nature_progres-768x1166.png",                             
                                                                            "bio-coherence": "/images/labels-vin/800px-Logo-bio-coherence.png",        
                                                                            "avn": "/images/labels-vin/logo-avn.png",           
                                                                            "methode-nature": "/images/labels-vin/Vin-methode-nature-logo.png",                                                                                                                                     
                                                                            "demeter": "/images/labels-vin/cropped-Demeter-logo.png",                                                                                                         
                                                                            "biodyvin": "/images/labels-vin/Biodyvin.png",         
                                                                            "sains": "/images/labels-vin/vins_s.a.i.n.s..png"
                                                                         }[label.toLowerCase()];
                                                                         
                                                                         return labelImage ? (
                                                                             <img 
                                                                                 key={label}
                                                                                 src={labelImage} 
                                                                                 alt={label}
                                                                                 style={{
                                                                                     height: 16 * sizeMultiplier,
                                                                                     width: 'auto',
                                                                                     marginLeft: 4 * sizeMultiplier,
                                                                                     display: 'inline',
                                                                                     verticalAlign: 'middle'
                                                                                 }}
                                                                             />
                                                                         ) : null;
                                                                     })
                                                                    }
                                                                </div>
                                                            }
                                                            {(() => {
                                                                const localizedPrices = getLocalizedMenuPrices(entry.entryPrice);
                                                                if (localizedPrices == null || hideDescriptionPrice) {
                                                                    return null;
                                                                }
                                                                const pricesList = Array.isArray(localizedPrices)
                                                                    ? localizedPrices
                                                                    : [localizedPrices];
                                                                return (
                                                                <div style={{width: textColumnWidth, textAlign: 'end', fontSize: 14 * sizeMultiplier, fontStyle: 'italic', color: '#707070', marginTop: 4 * sizeMultiplier, userSelect: 'none', WebkitUserSelect: 'none', ...priceColorStyle, ...textFontStyle}}>
                                                                    {    
                                                                    pricesList.map((price, priceIndex) => {
                                                                        const sep = priceIndex !== pricesList.length - 1 ? " - " : "";
                                                                        return <span key={`price-${entry.entryId}-${priceIndex}`} style={textFontStyle}>{price + sep}</span>
                                                                    })
                                                                    }
                                                                </div>
                                                                );
                                                            })()}
                                                        </div>
                                                        {img()}              
                                                    </div>  
                                                    {separator()}
                                                </div>              
                                            ); 
                                        }
                                        else {
                                            return null;
                                        }
                                    
                                    })
                                }  
                                {pageEntry.endingNote != null && 
                                    <div style={{width: menuContentWidth - 48 * sizeMultiplier, textAlign: endingNoteCentering, fontSize: 14 * sizeMultiplier, fontStyle: 'italic', color: '#707070', marginBottom: 20 * sizeMultiplier, ...descFontStyle, ...descColorStyle}}>
                                        <span style={descFontStyle} dangerouslySetInnerHTML={{__html: getLocalizedMenuText(pageEntry.endingNote)?.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '').replace(/\n/g, '<br>').replace(/<[^>]*>/g, (match) => {
                                            // Autoriser seulement les balises <b>, <i>, <u>, <strong>, <em>, <br>, <img>
                                            const allowedTags = ['b', 'i', 'u', 'strong', 'em', 'br', 'img'];
                                            const tagName = match.replace(/<\/?([^>\s]+).*/, '$1').toLowerCase();
                                            
                                            if (allowedTags.includes(tagName)) {
                                                // Pour les balises img, valider les attributs autorisés
                                                if (tagName === 'img') {
                                                    const sanitizedImg = match.replace(/<img([^>]*)>/gi, (fullMatch, attributes) => {
                                                        // Extraire et valider les attributs src, alt et height
                                                        const srcMatch = attributes.match(/src\s*=\s*["']([^"']*)["']/i);
                                                        const altMatch = attributes.match(/alt\s*=\s*["']([^"']*)["']/i);
                                                        const heightMatch = attributes.match(/height\s*=\s*["']([^"']*)["']/i);
                                                        
                                                        let sanitizedAttrs = '';
                                                        if (srcMatch && srcMatch[1]) {
                                                            // Valider que l'URL est sécurisée (http, https, ou chemin relatif)
                                                            const src = srcMatch[1];
                                                            if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('/') || src.startsWith('./')) {
                                                                sanitizedAttrs += ` src="${src}"`;
                                                            }
                                                        }
                                                        if (altMatch && altMatch[1]) {
                                                            sanitizedAttrs += ` alt="${altMatch[1]}"`;
                                                        }
                                                        if (heightMatch && heightMatch[1]) {
                                                            // Valider que la hauteur est un nombre ou un pourcentage
                                                            const height = heightMatch[1];
                                                            if (/^\d+(px|%|em|rem)?$/.test(height) || /^\d+$/.test(height)) {
                                                                sanitizedAttrs += ` height="${height}"`;
                                                            }
                                                        }
                                                        
                                                        return `<img${sanitizedAttrs}>`;
                                                    });
                                                    return sanitizedImg;
                                                }
                                                return match;
                                            }
                                            return '';
                                        })}} />
                                    </div>
                                }
                            </div> 
                        );  
                    }    
                    else {
                        return null;
                    }     
                }
                else if (pageEntry.entryType === "formula"){
                    const startingNoteOn2Lines = pageEntry.startingNote && getTextWidth(getLocalizedMenuText(pageEntry.startingNote), "" + 14 * sizeMultiplier + "px " + (resolvedTextFontFamily ? resolvedTextFontFamily : "Roboto")) > menuContentWidth - 2 * 24 * sizeMultiplier;
                    const endingNoteOn2Lines = pageEntry.endingNote && getTextWidth(getLocalizedMenuText(pageEntry.endingNote), "" + 14 * sizeMultiplier + "px " + (resolvedDescFontFamily ? resolvedDescFontFamily : "Roboto")) > menuContentWidth - 2 * 24 * sizeMultiplier;

                    const startingNoteCentering = startingNoteOn2Lines ? 'start' : 'center';
                    const endingNoteCentering = endingNoteOn2Lines ? 'start' : 'center';

                    const notFirstPageEntry = menuPages[pageNumber].pageEntries.findIndex((pEntry)=>pEntry.entriesTitle === pageEntry.entriesTitle) > 0;

                    let showAllergensSelector = ! allergensAlreadyDisplayed;
                    allergensAlreadyDisplayed = true; 

                    function formatFormula(){
                        const localizedEntriesTitle = getLocalizedMenuText(pageEntry.entriesTitle);
                        if (!localizedEntriesTitle) return;

                        const delimitor = language === "en" ? " or " : " ou ";                    
                        const lowerOverUpperCaseDelimitor = (localizedEntriesTitle.match(new RegExp(delimitor, "g")) || []).length > (localizedEntriesTitle.match(new RegExp(delimitor.toUpperCase(), "g")) || []).length                    
                        const usedDelimitor = language === "en" ? (lowerOverUpperCaseDelimitor ? " or " : " OR ") : (lowerOverUpperCaseDelimitor ? " ou " : " OU ");                 
                        const formulaChunks = localizedEntriesTitle.split(usedDelimitor);

                        const formattedFormula = formulaChunks.map((chunk, chunkIndex)=>{
                            const idx = formulaChunks.indexOf(chunk);
                            if (idx > 0 && idx < formulaChunks.length){ 
                                return (<React.Fragment key={`formula-${chunkIndex}`}><br/>{usedDelimitor}<br/>{chunk.trim()}</React.Fragment>);
                            }
                            else
                                return <React.Fragment key={`formula-${chunkIndex}`}>{chunk.trim()}</React.Fragment>;
                        });

                        return formattedFormula;
                    }

                    return (
                        <div key={`${pageNumber}-formula-${index}`}>
                            {menuHasAllergens && showAllergensSelector && 
                                <div style={{width: "100%", display: 'flex',  display: menuPages[pageNumber].showAllergensFilter ? '' : 'none', flexDirection: 'row', justifyContent: 'center', marginBottom: 20}}>
                                    <AllergensSelect initialValue={filteredAllergens.split(",")} color={props.story.primaryColor} callback={(values)=> {setFilteredAllergens(values)}}/>
                                </div>
                            } 
                            {pageEntry.entriesTitle != null && 
                                <div style={{marginTop: notFirstPageEntry ? 10 * sizeMultiplier : 0, marginBottom: 20 * sizeMultiplier, textAlign: 'center', fontSize: 24 * sizeMultiplier, color: '#404040', ...textColorStyle}}>
                                    {formatFormula()}
                                    {pageEntry.formulaPrice &&
                                        <div style={{marginTop: 10 * sizeMultiplier, marginBottom: 0, textAlign: 'center', fontSize: 20 * sizeMultiplier, color: '#404040', userSelect: 'none', WebkitUserSelect: 'none', ...textFontStyle, ...priceColorStyle}}>
                                            {pageEntry.formulaPrice}
                                        </div>
                                    }
                                </div>
                            }
                            {pageEntry.startingNote != null && 
                                <div style={{width: menuContentWidth - 48 * sizeMultiplier, textAlign: startingNoteCentering, fontSize: 14 * sizeMultiplier, fontStyle: 'italic', color: '#707070', marginBottom: 30 * sizeMultiplier, ...textFontStyle, ...textColorStyle}}>
                                    {getLocalizedMenuText(pageEntry.startingNote)}
                                </div>
                            }
                            {pageEntry.entries && pageEntry.entries.map((entry, entryIndex) => {
                                    
                                    if (entry.entryTitle != null){
                                        const textColumnWidth = menuContentWidth - 48 * sizeMultiplier;
                                        const entryTitleInfo = getEntryTitleInfo(entry);
                                        const entryTitle = entryTitleInfo.cleanTitle;

                                        const separator = () => {
                                            const entriesNb = pageEntry.entries.length;
                                            const notLastPosition = entry.entryId !== entriesNb - 1;
                                            const nextPositionIsNotSeparator = entry.entryId < entriesNb - 1 && pageEntry.entries[entry.entryId + 1].separator !== true;                                    
                                                                                    
                                            if (notLastPosition && nextPositionIsNotSeparator){
                                                return (
                                                    <div
                                                        style={{
                                                            marginTop: 20 * sizeMultiplier,
                                                            marginBottom: 20 * sizeMultiplier,
                                                            width: 60 * sizeMultiplier,
                                                            height: 2,
                                                            backgroundColor: addOpacityToHexColor(menuColor, 0.2),
                                                            borderRadius: 999,
                                                        }}
                                                    />
                                                );
                                            }
                                            else if (! notLastPosition){
                                                return (
                                                    <div style={{marginTop: 20 * sizeMultiplier}} />
                                                );
                                            }
                                            else return null;            
                                        };                                    
                                        
                                        return(                   
                                            <div key={pageNumber + "-" + entry.entryId} className='menu-line-sep-container' >
                                                <div className='menu-line-container'>
                                                    <div className='menu-line-container-wo-img' style={{width: textColumnWidth, pointerEvents: (checkMobile() || playMode === "demo") ? 'none' : 'auto', /*height: entryImageDimensions*/}}>                                                      
                                                        <div className="menu-entry-title" style={{width: textColumnWidth, textAlign: "center", fontSize: entryTitleFontSizePx, color: '#404040', ...textFontStyle, ...textColorStyle, ...textFontBoldStyle}}>
                                                            {entryTitle}
                                                        </div>
                                                        {entry.entrySubtitle && !hideMenuDescription && <div className="menu-entry-subtitle" style={{width: textColumnWidth, /*height: 34 * sizeMultiplier,*/ textAlign: "center", fontSize: 14 * sizeMultiplier, fontStyle: 'italic', color: '#707070', marginTop: 4 * sizeMultiplier, ...descFontStyle, ...descColorStyle, ...(entry.entryImage != null ? {display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, lineClamp: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'normal'} : {whiteSpace: 'pre-line'})}}>{getLocalizedMenuText(entry.entrySubtitle)}</div>}
                                                        {entryTitleInfo.isVegetarian &&
                                                            <div style={{marginTop: 8 * sizeMultiplier, width: textColumnWidth, textAlign: "center"}}>
                                                                <span style={{display: 'inline-flex', alignItems: 'center', gap: 6 * sizeMultiplier, fontSize: 12 * sizeMultiplier, fontWeight: 500, color: '#6a6a6a', userSelect: 'none', WebkitUserSelect: 'none'}}>
                                                                    <span style={{width: 6 * sizeMultiplier, height: 6 * sizeMultiplier, borderRadius: '50%', backgroundColor: '#8a8a8a'}} />
                                                                    {getVegetarianLabel()}
                                                                </span>
                                                            </div>
                                                        }
                                                    </div>       
                                                </div>  
                                                {separator()}
                                            </div>              
                                        );
                                    } 
                                    else if (entry.separator === true){
                                        return (
                                            <div key={`${pageNumber}-separator-${entry.entryId || entryIndex}`} style={{display: 'flex', flexDirection: 'row', justifyContent: 'center'}}>
                                                <div
                                                    style={{
                                                        marginTop: 20 * sizeMultiplier,
                                                        marginBottom: 20 * sizeMultiplier,
                                                        width: (menuContentWidth - 48 * sizeMultiplier) * 0.75,
                                                        height: 2,
                                                        backgroundColor: addOpacityToHexColor(menuColor, 0.5),
                                                        borderRadius: 999,
                                                    }}
                                                />
                                            </div>
                                        );
                                    }
                                    else {
                                        return null;
                                    }
                                
                                })
                            }  
                            {pageEntry.endingNote != null && 
                                <div style={{width: menuContentWidth - 48 * sizeMultiplier, textAlign: endingNoteCentering, fontSize: 14 * sizeMultiplier, fontStyle: 'italic', color: '#707070', marginTop: 10, marginBottom: 30 * sizeMultiplier, ...descFontStyle, ...descColorStyle}}>
                                    <span style={descFontStyle} dangerouslySetInnerHTML={{__html: getLocalizedMenuText(pageEntry.endingNote)?.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '').replace(/\n/g, '<br>').replace(/<[^>]*>/g, (match) => {
                                        // Autoriser seulement les balises <b>, <i>, <u>, <strong>, <em>, <br>, <img>
                                        const allowedTags = ['b', 'i', 'u', 'strong', 'em', 'br', 'img'];
                                        const tagName = match.replace(/<\/?([^>\s]+).*/, '$1').toLowerCase();
                                        
                                        if (allowedTags.includes(tagName)) {
                                            // Pour les balises img, valider les attributs autorisés
                                            if (tagName === 'img') {
                                                    const sanitizedImg = match.replace(/<img([^>]*)>/gi, (fullMatch, attributes) => {
                                                        // Extraire et valider les attributs src, alt et height
                                                        const srcMatch = attributes.match(/src\s*=\s*["']([^"']*)["']/i);
                                                        const altMatch = attributes.match(/alt\s*=\s*["']([^"']*)["']/i);
                                                        const heightMatch = attributes.match(/height\s*=\s*["']([^"']*)["']/i);
                                                        
                                                        let sanitizedAttrs = '';
                                                        if (srcMatch && srcMatch[1]) {
                                                            // Valider que l'URL est sécurisée (http, https, ou chemin relatif)
                                                            const src = srcMatch[1];
                                                            if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('/') || src.startsWith('./')) {
                                                                sanitizedAttrs += ` src="${src}"`;
                                                            }
                                                        }
                                                        if (altMatch && altMatch[1]) {
                                                            sanitizedAttrs += ` alt="${altMatch[1]}"`;
                                                        }
                                                        if (heightMatch && heightMatch[1]) {
                                                            // Valider que la hauteur est un nombre ou un pourcentage
                                                            const height = heightMatch[1];
                                                            if (/^\d+(px|%|em|rem)?$/.test(height) || /^\d+$/.test(height)) {
                                                                sanitizedAttrs += ` height="${height}"`;
                                                            }
                                                        }
                                                        
                                                        return `<img${sanitizedAttrs}>`;
                                                    });
                                                return sanitizedImg;
                                            }
                                            return match;
                                        }
                                        return '';
                                    })}} />
                                </div>
                            }
                        </div> 
                    );
                }            
                else if (pageEntry.entryType === "note"){                  
                    const centering = pageEntry.centering != null ? pageEntry.centering : 'center';

                    function formatNoteText(){    
                        if (!getLocalizedMenuText(pageEntry.text)) return;
                        
                        // Appliquer la même logique de sécurité et de formatage que pour les autres champs
                        const sanitizedText = getLocalizedMenuText(pageEntry.text)
                            .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Supprimer les scripts
                            .replace(/\n/g, '<br>') // Convertir les \n en <br>
                            .replace(/<[^>]*>/g, (match) => {
                                // Autoriser seulement les balises <b>, <i>, <u>, <strong>, <em>, <br>
                                const allowedTags = ['b', 'i', 'u', 'strong', 'em', 'br'];
                                const tagName = match.replace(/<\/?([^>\s]+).*/, '$1').toLowerCase();
                                return allowedTags.includes(tagName) ? match : '';
                            });

                        return <span style={descFontStyle} dangerouslySetInnerHTML={{__html: sanitizedText}} />;
                    }

                    return(
                        <div key={`${pageNumber}-note-entry-${index}`} style={{marginTop: 0}}>
                            <div style={{width: menuContentWidth - 48 * sizeMultiplier, whiteSpace: 'pre-line', textAlign: centering, fontSize: 14 * sizeMultiplier, fontStyle: 'italic', color: '#707070', marginBottom: 30 * sizeMultiplier, userSelect: 'none', WebkitUserSelect: 'none', ...descFontStyle, ...descColorStyle}}>
                                {formatNoteText()}
                                {pageEntry.allergens && 
                                    <><br/><br/><a href={pageEntry.allergens} target="_blank">{t('MenuAllergens')}</a></>
                                }
                            </div>
                            {props.story.avatarUrl &&
                                <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'center'}}>
                                    <img alt={t('RestaurantAvatarAlt')} src={props.story.avatarUrl} className="menu-logo-container" draggable={false} style={{borderColor: props.story.primaryColor + "28", userSelect: 'none', WebkitUserSelect: 'none', WebkitUserDrag: 'none'}}/>
                                </div> 
                            }
                        </div>
                    );
                }
                else {
                    return null;
                }                
            });
        }
    }

    const borders = () => {
        if (props.story.menuStyle === "elegant"){
            return (
                <div>
                    <Image key="border-left" source="/images/elegant_border.png" style={{position: 'absolute', top:0, left: 0, width: 80 * sizeMultiplier, height: 80 * sizeMultiplier, transform: 'scaleX(-1)', WebkitTransform: 'scaleX(-1)', opacity:0.4}} tintColor={menuColor}/>
                    <Image key="border-right" source="/images/elegant_border.png" style={{position: 'absolute', top:0, right: 0, width: 80 * sizeMultiplier, height: 80 * sizeMultiplier, opacity:0.4}} tintColor={menuColor}/>                    
                </div>
            );
        }
        else return null;    
    };      

    const marginBtwTitleList = props.story.menuStyle === "modern" && menuPages != null ? (((36 + 66) * sizeMultiplier + menuPages[pageNumber].pageEntries.length * 72 * sizeMultiplier + (menuPages[pageNumber].pageEntries.length - 1) * 41 * sizeMultiplier + 20 * sizeMultiplier) > (storyDimensions.containerHeight ? 112 : 46) * sizeMultiplier) : 56 * sizeMultiplier;    

    if (props.show){  
        // scroll up closes the webpage otherwise disallowing us to scroll up the page before the snapchat browser scroll up to close feature
        // overflow visible allow to make the page scrollable, then we set it to hidden on menu close (setting back to auto would make the story video scrollable) 
        if (isSnapchatInAppBrowser() && isAndroid() || isIOS() && isInstagramInAppBrowser() || isIOS() && isSnapchatInAppBrowser()){                
            document.getElementsByClassName("StoryVideo")[0].style.display = "none";
            const shadows = document.getElementById("menu-shadows");
            if (shadows) shadows.style.display = "none";
            document.getElementsByClassName("StoryVideoContainer")[0].style.overflow = "visible";  
            if (isIOS() && isSnapchatInAppBrowser()){
                document.getElementsByTagName('body')[0].style.backgroundColor = "#FFFFFF";
                const blockingOverlays = document.getElementsByClassName("BlockingOverlay");                
                if (blockingOverlays && blockingOverlays[0])
                    blockingOverlays[0].style.display = "none"; 
                const galleryContainer = document.getElementById("gallery-container");
                if (galleryContainer){
                    galleryContainer.style.display = "none";
                }                
            }              
        }     
        
        const isSnapchatIos = isIOS() && isSnapchatInAppBrowser();
        const menuContainerBackgroundStyle = (props.story.menuStyle === "custom" && menu && menu.backgroundColor)
            ? { backgroundColor: menu.backgroundColor }
            : {};

        const blockMenuInteraction = (e) => e.stopPropagation();

        const menuRootStyle = {
            width: storyDimensions.containerWidth,
            height: storyDimensions.containerHeight,
            pointerEvents: 'auto',
            zIndex: 30,
            // on iOS with Snapchat we avoid absolute positioning to prevent scroll-up-to-close
            ...(!isSnapchatIos ? { position: 'absolute', top: 0, left: 0 } : {}),
        };

        const menuFrameStyle = {
            width: '100%',
            height: '100%',
            padding: menuOuterMargin,
            boxSizing: 'border-box',
            ...(isMobileDevice ? { backgroundColor: '#FFFFFFD0' } : {}),
            pointerEvents: 'auto',
        };
        const menuContainerStyle = {
            width: '100%',
            height: '100%',
            position: 'relative',
            borderRadius: isMobileDevice ? 24 * sizeMultiplier : 0,
            overflowX: 'hidden',
            ...(!isSnapchatIos ? { overflowY: 'auto' } : {}),
            ...menuContainerBackgroundStyle,
        };

        const listItemsMarginTop = marginBtwTitleList + (props.story.menuStyle === "modern" ? 30 : 0);             

        const swipeToTurn = !(isTikTokInAppBrowser() && isIOS() && !isAndroid() || isFirefox() && isAndroid() || isIpad() && isInstagramInAppBrowser());
        const enableTouchSwipeToTurn = swipeToTurn;

        let closeColor = menuColor;
        if (props.story.menuStyle === "custom" && menu && menu.closeColor && !pageEntry){
            closeColor = menu.closeColor;
        }
        if (props.story.menuStyle === "custom" && menu && menu.closeColor && pageEntry){
            closeColor = menu.closeColor;
            }             
        if (props.story.menuStyle === "custom" && menu && menu.detailCloseColor && pageEntry){
            closeColor = menu.detailCloseColor;
        }

        const isImagePage = menuPages && menuPages[pageNumber] && menuPages[pageNumber].pageType && menuPages[pageNumber].pageType.fr && menuPages[pageNumber].pageType.fr === "image";
        const isRichTextPage = menuPages && menuPages[pageNumber] && isRichTextMenuPage(menuPages[pageNumber]);
        const isSpecialLayoutPage = isImagePage || isRichTextPage;
        const navButtonStyle = {
            width: 40 * sizeMultiplier,
            height: 40 * sizeMultiplier,
            backgroundColor: addOpacityToHexColor(menuColor, 0.9),
            border: `1px solid ${addOpacityToHexColor("#ffffff", 0.35)}`,
        };
        const navIconSize = 24 * sizeMultiplier;
        const pageTransitionEnabled = !prefersReducedMotion && !pageEntry;
        const menuBackgroundColor = (props.story.menuStyle === "custom" && menu && menu.backgroundColor) ? menu.backgroundColor : "#FFFFFF";

        return ( 
            <motion.div
                style={menuRootStyle}
                initial={prefersReducedMotion ? false : { opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: menuOpenFadeDuration, ease: "easeOut" }}
                onClick={blockMenuInteraction}
                onTouchStart={blockMenuInteraction}
            >
                <Snackbar
                    open={showPreviewOptimInfo}
                    autoHideDuration={9000}
                    onClose={() => setShowPreviewOptimInfo(false)}
                    anchorOrigin={{vertical: 'top', horizontal: 'right'}}
                >
                    <Alert
                        onClose={() => setShowPreviewOptimInfo(false)}
                        severity="info"
                        sx={{ width: '100%', whiteSpace: 'pre-line' }}
                    >
                        {t('InfoMenuPreviewOptimization')}
                    </Alert>
                </Snackbar>
                {  !pageEntry &&
                    <div style={menuFrameStyle}>
                        <div id="menu-container" className={'menu-container' + ((checkMobile() || playMode === "demo") ? ' menu-container-scroll-mobile' : ' menu-container-scroll-desktop')} style={menuContainerStyle} onTouchStart={enableTouchSwipeToTurn ? onTouchStart : null} onTouchMove={enableTouchSwipeToTurn ? onTouchMove : null} onTouchEnd={enableTouchSwipeToTurn ?  onTouchEnd : null} >
                        {playMode === "preview" && !checkMobile() && <div style={{width: 1, height: '100%', backgroundColor: '#b2ede7', position: 'absolute', left: 0, bottom: 0}} />}                 
                        {borders()}
                        <AnimatePresence initial={false} mode="wait" custom={pageTransitionDirection}>
                            <motion.div
                                key={`menu-page-${pageNumber}`}
                                custom={pageTransitionDirection}
                                initial={pageTransitionEnabled ? { opacity: 0.995 } : false}
                                animate={{ opacity: 1 }}
                                exit={pageTransitionEnabled ? { opacity: 0.995 } : false}
                                transition={{ duration: pageRevealDuration, ease: [0.22, 0.61, 0.36, 1] }}
                                style={{width: "100%", position: "relative", ...(isSpecialLayoutPage ? { minHeight: menuContentHeight } : {})}}
                            >
                                {pageTransitionEnabled && (
                                    <motion.div
                                        key={`menu-page-mask-${pageNumber}`}
                                        initial={{
                                            scaleX: 1,
                                            originX: pageTransitionDirection >= 0 ? 0 : 1,
                                        }}
                                        animate={{ scaleX: 0 }}
                                        transition={{ duration: pageRevealDuration, ease: [0.22, 0.61, 0.36, 1] }}
                                        style={{
                                            position: "absolute",
                                            inset: 0,
                                            backgroundColor: menuBackgroundColor,
                                            zIndex: 2,
                                            pointerEvents: "none",
                                            willChange: "transform",
                                        }}
                                    />
                                )}
                                {!isSpecialLayoutPage && (
                                    <div>
                                        {title()}
                                        <div className="menu-items" style={{marginTop: menuPages && menuPages[pageNumber]&& menuPages[pageNumber].pageType && menuPages[pageNumber].pageType.fr && menuPages[pageNumber].pageType.fr !== "" ? listItemsMarginTop : 0, marginLeft: 24 * sizeMultiplier, marginBottom: 60 * sizeMultiplier, width: menuContentWidth - 48 * sizeMultiplier, marginRight: 24 * sizeMultiplier}}>
                                            {listItems}
                                        </div>
                                    </div>
                                )}
                                {isSpecialLayoutPage && (
                                    <div className="menu-items" style={{marginTop: 0, marginLeft: 0, marginBottom: isRichTextPage ? 0 : 60 * sizeMultiplier, width: menuContentWidth, marginRight: 0, position: 'relative', minHeight: menuContentHeight}}>
                                        {listItems}
                                    </div>
                                )}
                            </motion.div>
                        </AnimatePresence>
                        </div>
                    </div>
                }
                <MealDetail fromMenuEditor={props.fromMenuEditor ? true : false} story={props.story} storyId={props.storyId} pageEntry={pageEntry} setPageEntry={setPageEntry} menuColor={props.story.menuColor} storyDimensions={storyDimensions} menu={menu} closeMenuColor={closeColor} close={closeMenuOrDetail} displayTextFont={textFont} displayTextFallback={menuTextFontFallback} displayDescFont={descFont} displayDescFallback={menuDescFontFallback}/>
                
                {!pageEntry &&(pageNumber > 0) && 
                        <div style={{position: 'absolute', zIndex: 30, pointerEvents: "auto", top: storyDimensions.containerHeight - 40 * sizeMultiplier - 14 * sizeMultiplier - menuOuterMargin, left: storyDimensions.containerWidth/2 - (48+8) * sizeMultiplier, }}>
                            <IconButton aria-label="page left" disabled={isPageTransitioning} style={navButtonStyle} onClick={() => {
                                if (!requestPageChange(-1)) return;
                                analyticsManager.logSwipeFullMenuTag(props.storyId, playMode);
                                if (getStoredPixelConsent() === 'accepted') {
                                    if (window.ttq){
                                        window.ttq.track('ViewContent', {                
                                            "contents": [
                                                {              
                                                    "content_id": "0",           
                                                    "content_name": "change menu page for " + (pageNumber - 1),
                                                }
                                            ],
                                        });
                                    }
                                    if (window.fbq){                    
                                        window.fbq('track', 'ViewContent', {"content_name": "change menu page for " + (pageNumber - 1)});
                                    }
                                }
                                if (isImagePage){
                                    transformComponentRef.current.resetTransform();
                                }
                                }}>
                                <ChevronLeft style={{color: "#ffffff", width: navIconSize, height: navIconSize}}/>
                            </IconButton>
                        </div> 
                }
               {!pageEntry && menu && (pageNumber < menu.menuPages.length - 1) && 
                        <div style={{position: 'absolute', zIndex: 30, pointerEvents: "auto", top: storyDimensions.containerHeight - 40 * sizeMultiplier - 14 * sizeMultiplier - menuOuterMargin, left: storyDimensions.containerWidth/2 + 8 * sizeMultiplier, }}>
                            <IconButton aria-label="page right" disabled={isPageTransitioning} style={navButtonStyle} onClick={() => {
                                if (!requestPageChange(1)) return;
                                analyticsManager.logSwipeFullMenuTag(props.storyId, playMode);
                                if (getStoredPixelConsent() === 'accepted') {
                                    if (window.ttq){
                                        window.ttq.track('ViewContent', {                
                                            "contents": [
                                                {              
                                                    "content_id": "0",           
                                                    "content_name": "change menu page for " + (pageNumber + 1),
                                                }
                                            ],
                                        });
                                    }
                                    if (window.fbq){                    
                                        window.fbq('track', 'ViewContent', {"content_name": "change menu page for " + (pageNumber + 1)});
                                    }
                                }
                                if (isImagePage){
                                    transformComponentRef.current.resetTransform();
                                }
                                }}>
                                <ChevronRight style={{color: "#ffffff", width: navIconSize, height: navIconSize}}/>
                            </IconButton>
                        </div>
                } 
                {!pageEntry && 
                    <div style={{position: 'absolute', zIndex: 30, pointerEvents: "auto", top: storyDimensions.containerHeight - 40 * sizeMultiplier - 14 * sizeMultiplier - menuOuterMargin, left: storyDimensions.containerWidth - 2 * menuOuterMargin - 48 * sizeMultiplier, }}>                        
                        <IconButton aria-label="close menu" style={{...navButtonStyle, backgroundColor: addOpacityToHexColor(closeColor, 0.9), border: `1px solid ${addOpacityToHexColor("#ffffff", 0.35)}`}} onClick={(e) => closeMenuOrDetail(e)}>
                            <Close style={{color: "#ffffff", width: navIconSize, height: navIconSize}}/>
                        </IconButton>                                        
                    </div>   
                }                       
            </motion.div>
        );
    }
    else {
        return null;        
    }   

    function closeMenuOrDetail(e){
        if (pageEntry != null){
            analyticsManager.logOpenDishDetalTag(props.storyId, playMode, false);
            setPageEntry(null);            
        }
        else closeMenu(e);
    }
    
    function closeMenu(e){        
        if (isSnapchatInAppBrowser() && isAndroid() || isIOS() && isInstagramInAppBrowser() || isIOS() && isSnapchatInAppBrowser()){                            
            document.getElementsByClassName("StoryVideoContainer")[0].style.overflow = "hidden"; 
            document.getElementsByClassName("StoryVideo")[0].style.display = "";
            const shadows = document.getElementById("menu-shadows");
            if (shadows) shadows.style.display = "";
            if (isIOS() && isSnapchatInAppBrowser()){                       
                document.getElementsByTagName('body')[0].style.backgroundColor = addOpacityToHexColor(props.story.primaryColor, 0.2);
                const blockingOverlays = document.getElementsByClassName("BlockingOverlay");                
                if (blockingOverlays && blockingOverlays[0])
                    blockingOverlays[0].style.display = ""; 
                const galleryContainer = document.getElementById("gallery-container");
                if (galleryContainer){
                    galleryContainer.style.display = "";
                }
            }
        }      
        props.closeMenu();        
    }

    function getDemoProductPages(){ 
        return [
            /*{
                pageType: {fr: "Menus", en: "Menus"},
                pageEntries: [  
                    {
                        entryType: "startingNote",
                        text: "starting note",
                        centering: 'center'
                    },                 
                    {
                        entryType: "formula",
                        entriesTitle: {
                            fr: t('MenuDemoMenusTitle', {menu: "A"}),
                            en: t('MenuDemoMenusTitle', {menu: "A"}),
                        },
                        startingNote: "starting note",                        
                        entries: [
                            {
                                entryId: 0,
                                entryTitle: {
                                    fr: t('MenuDemoStarterTitle', {nb: 1}),
                                    en: t('MenuDemoStarterTitle', {nb: 1})
                                },
                            },
                            {
                                entryId: 1,
                                entryTitle: {
                                    fr: t('MenuDemoStarterTitle', {nb: 2}),
                                    en: t('MenuDemoStarterTitle', {nb: 2})
                                },
                            },
                            {
                                entryId: 2,
                                separator: true,
                            },
                            {
                                entryId: 3,
                                entryTitle: {
                                    fr: t('MenuDemoMainCourseTitle', {nb: 1}),
                                    en: t('MenuDemoMainCourseTitle', {nb: 1})
                                },
                            }, 
                            {
                                entryId: 4,
                                entryTitle: {
                                    fr: t('MenuDemoMainCourseTitle', {nb: 2}),
                                    en: t('MenuDemoMainCourseTitle', {nb: 2})
                                },
                            }, 
                            {
                                entryId: 5,
                                separator: true,
                            },
                            {
                                entryId: 6,
                                entryTitle: {
                                    fr: t('MenuDemoDessertTitle', {nb: 1}),
                                    en: t('MenuDemoDessertTitle', {nb: 1}),
                                },
                            }, 
                            {
                                entryId: 7,
                                entryTitle: {
                                    fr: t('MenuDemoDessertTitle', {nb: 2}),
                                    en: t('MenuDemoDessertTitle', {nb: 2}),
                                },
                            },                                 
                        ],    
                        endingNote: "ending note",                        
                    },
                    {
                        entryType: "formula",
                        entriesTitle: {
                            fr: t('MenuDemoMenusTitle', {menu: "B"}),
                            en: t('MenuDemoMenusTitle', {menu: "B"}),
                        },
                        entries: [
                            {
                                entryId: 0,
                                entryTitle: {
                                    fr: t('MenuDemoMenusDesc1'),
                                    en: t('MenuDemoMenusDesc1'),
                                },                                
                            },
                        ]
                    },                                     
                    {
                        entryType: "endingNote",
                        text: "ending note",
                        centering: 'center'
                    },
                ]
            },  */    
            /*{
                pageType: {fr: "image", en: "image"}, 
                pageImgUrl: "https://storage.googleapis.com/upreality-prod-619c3.appspot.com/restaurants/WgpXG6xvvYUdpit8zuK1PDDmXNM2/dMS24cQaTXn47tuspxet/menu/Menu-reveillon-Caffe.png",
                pageBackground: "#9ECEC9",
            },  */   
            {
                pageType: {fr: "Entrées", en: "Starters"}, 
                pageTypeImg: "/images/starter_demo.png",                               
                pageEntries: [
                    /*{
                        entryType: "startingNote",
                        text: "starting note",
                        centering: 'center'
                    },*/
                    {
                        entryType: "dishList",                        
                        /*entriesTitle: "Salades",
                        startingNote: "starting note",*/
                        entries: [
                            {
                                entryId: 0,
                                entryTitle: {
                                    fr: t('MenuDemoStarterTitleDemo1', {nb: 1}),
                                    en: t('MenuDemoStarterTitleDemo1', {nb: 1})
                                },                                
                                entrySubtitle: {
                                    fr: t('MenuDemoStarterDescDemo1', {nb: 1}),
                                    en: t('MenuDemoStarterDescDemo1', {nb: 1})
                                },
                                entryPrice: {fr: ["9 €"], en: ["€9"]},                                
                                // entryImage: "/images/img_starter.jpg",
                            },
                            {
                                entryId: 1,
                                entryTitle: {
                                    fr: t('MenuDemoStarterTitleDemo2', {nb: 2}),
                                    en: t('MenuDemoStarterTitleDemo2', {nb: 2})
                                },                                
                                entrySubtitle: {
                                    fr: t('MenuDemoStarterDescDemo2', {nb: 2}),
                                    en: t('MenuDemoStarterDescDemo2', {nb: 2})
                                },  
                                entryPrice: {fr: ["10 €"], en: ["€10"]},
                            },
                            {
                                entryId: 2,
                                entryTitle: {
                                    fr: t('MenuDemoStarterTitleDemo3', {nb: 3}),
                                    en: t('MenuDemoStarterTitleDemo3', {nb: 3})
                                },                                
                                entrySubtitle: {
                                    fr: t('MenuDemoStarterDescDemo3', {nb: 3}),
                                    en: t('MenuDemoStarterDescDemo3', {nb: 3})
                                }, 
                                entryPrice: {fr: ["10 €"], en: ["€10"]},
                                // entryImage: "/images/img_terrine_campagne.jpg",
                            },
                            {
                                entryId: 3,
                                entryTitle: {
                                    fr: t('MenuDemoStarterTitleDemo4', {nb: 4}),
                                    en: t('MenuDemoStarterTitleDemo4', {nb: 4})
                                },                                
                                entrySubtitle: {
                                    fr: t('MenuDemoStarterDescDemo4', {nb: 4}),
                                    en: t('MenuDemoStarterDescDemo4', {nb: 4})
                                },
                                entryPrice: {fr: ["7 €"], en: ["€7"]},
                            },
                            {
                                entryId: 4,
                                entryTitle: {
                                    fr: t('MenuDemoStarterTitleDemo5', {nb: 5}),
                                    en: t('MenuDemoStarterTitleDemo5', {nb: 5})
                                },                                
                                entrySubtitle: {
                                    fr: t('MenuDemoStarterDescDemo5', {nb: 5}),
                                    en: t('MenuDemoStarterDescDemo5', {nb: 5})
                                },
                                entryPrice: {fr: ["12 €"], en: ["€12"]},
                                // entryImage: "/images/img_moules_marinieres.jpg",
                            },
                            /*{
                                entryId: 41,
                                entryTitle: {
                                    fr: t('MenuDemoStarterTitle', {nb: 5}),
                                    en: t('MenuDemoStarterTitle', {nb: 5})
                                },                                
                                entrySubtitle: {
                                    fr: t('MenuDemoStarterDesc', {nb: 5}),
                                    en: t('MenuDemoStarterDesc', {nb: 5})
                                },
                                entryPrice: {fr: ["X €"], en: ["X €"]},
                                // entryImage: "/images/img_starter.jpg",
                            },
                            {
                                entryId: 42,
                                entryTitle: {
                                    fr: t('MenuDemoStarterTitle', {nb: 5}),
                                    en: t('MenuDemoStarterTitle', {nb: 5})
                                },                                
                                entrySubtitle: {
                                    fr: t('MenuDemoStarterDesc', {nb: 5}),
                                    en: t('MenuDemoStarterDesc', {nb: 5})
                                },
                                entryPrice: {fr: ["X €"], en: ["X €"]},
                                // entryImage: "/images/img_starter.jpg",
                            },
                            {
                                entryId: 43,
                                entryTitle: {
                                    fr: t('MenuDemoStarterTitle', {nb: 5}),
                                    en: t('MenuDemoStarterTitle', {nb: 5})
                                },                                
                                entrySubtitle: {
                                    fr: t('MenuDemoStarterDesc', {nb: 5}),
                                    en: t('MenuDemoStarterDesc', {nb: 5})
                                },
                                entryPrice: {fr: ["X €"], en: ["X €"]},
                                // entryImage: "/images/img_starter.jpg",
                            },
                            {
                                entryId: 44,
                                entryTitle: {
                                    fr: t('MenuDemoStarterTitle', {nb: 5}),
                                    en: t('MenuDemoStarterTitle', {nb: 5})
                                },                                
                                entrySubtitle: {
                                    fr: t('MenuDemoStarterDesc', {nb: 5}),
                                    en: t('MenuDemoStarterDesc', {nb: 5})
                                },
                                entryPrice: {fr: ["X €"], en: ["X €"]},
                                // entryImage: "/images/img_starter.jpg",
                            },
                            {
                                entryId: 45,
                                entryTitle: {
                                    fr: t('MenuDemoStarterTitle', {nb: 5}),
                                    en: t('MenuDemoStarterTitle', {nb: 5})
                                },                                
                                entrySubtitle: {
                                    fr: t('MenuDemoStarterDesc', {nb: 5}),
                                    en: t('MenuDemoStarterDesc', {nb: 5})
                                },
                                entryPrice: {fr: ["X €"], en: ["X €"]},
                                // entryImage: "/images/img_starter.jpg",
                            },   */                 
                        ],
                        /*endingNote: "ending note",*/
                    },
                    /*{
                        entryType: "dishList",
                        entriesTitle: "Antipasti",
                        startingNote: "starting note",                        
                        entries: [
                            {
                                entryId: 0,
                                entryTitle: t('MenuDemoStarterTitle', {nb: 1}),
                                entrySubtitle: t('MenuDemoStarterDesc', {nb: 1}),
                                entryPrice: {fr:["37.5 cl : X €", "75 cl : X €"],en:["37.5 cl : X €", "75 cl : X €"]},
                                // entryImage: "/images/img_starter.jpg",
                            },
                            {
                                entryId: 1,
                                entryTitle: t('MenuDemoStarterTitle', {nb: 2}),
                                entrySubtitle: t('MenuDemoStarterDesc', {nb: 2}),                                
                            },
                            {
                                entryId: 2,
                                entryTitle: t('MenuDemoStarterTitle', {nb: 3}),
                                entrySubtitle: t('MenuDemoStarterDesc', {nb: 3}),
                                entryPrice: {fr: ["X €"], en: ["X €"]},
                                entryImage: "/images/img_starter.jpg",
                            },
                            {
                                entryId: 3,
                                entryTitle: t('MenuDemoStarterTitle', {nb: 4}),
                                entrySubtitle: t('MenuDemoStarterDesc', {nb: 4}),
                                entryPrice: {fr: ["X €"], en: ["X €"]},
                            },
                            {
                                entryId: 4,
                                entryTitle: t('MenuDemoStarterTitle', {nb: 5}),
                                entrySubtitle: t('MenuDemoStarterDesc', {nb: 5}),
                                entryPrice: {fr: ["X €"], en: ["X €"]},
                                entryImage: "/images/img_starter.jpg",
                            },                    
                        ],
                        endingNote: "ending note",                        
                    },
                    {
                        entryType: "endingNote",
                        text: "ending note",
                        centering: 'center'
                    },*/
                ]
            },
            {                
                pageType: {fr: "Plats", en: "Dishes"},
                pageTypeImg: "/images/main_demo.png",
                pageEntries: [
                    {
                        entryType: "dishList",
                        entries: [
                            {
                                entryId: 0,
                                entryTitle: {
                                    fr: t('MenuDemoMainCourseTitleDemo1', {nb: 1}),
                                    en: t('MenuDemoMainCourseTitleDemo1', {nb: 1})
                                },                                
                                entrySubtitle: {
                                    fr: t('MenuDemoMainCourseDescDemo1', {nb: 1}),
                                    en: t('MenuDemoMainCourseDescDemo1', {nb: 1})
                                },
                                entryPrice: {fr: ["19 €"], en: ["€19"]},
                                signature: true,
                                // entryImage: "/images/img_maincourse.jpg",
                            },
                            {
                                entryId: 1,
                                entryTitle: {
                                    fr: t('MenuDemoMainCourseTitleDemo2', {nb: 2}),
                                    en: t('MenuDemoMainCourseTitleDemo2', {nb: 2})
                                },                                
                                entrySubtitle: {
                                    fr: t('MenuDemoMainCourseDescDemo2', {nb: 2}),
                                    en: t('MenuDemoMainCourseDescDemo2', {nb: 2})
                                },
                                entryPrice: {fr: ["17 €"], en: ["€17"]},
                            },
                            {
                                entryId: 2,
                                entryTitle: {
                                    fr: t('MenuDemoMainCourseTitleDemo3', {nb: 3}),
                                    en: t('MenuDemoMainCourseTitleDemo3', {nb: 3})
                                },                                
                                entrySubtitle: {
                                    fr: t('MenuDemoMainCourseDescDemo3', {nb: 3}),
                                    en: t('MenuDemoMainCourseDescDemo3', {nb: 3})
                                },
                                entryPrice: {fr: ["20 €"], en: ["€20"]},
                                // entryImage: "/images/img_confit_canard.jpg",
                            },
                            {
                                entryId: 3,
                                entryTitle: {
                                    fr: t('MenuDemoMainCourseTitleDemo4', {nb: 4}),
                                    en: t('MenuDemoMainCourseTitleDemo4', {nb: 4})
                                },                                
                                entrySubtitle: {
                                    fr: t('MenuDemoMainCourseDescDemo4', {nb: 4}),
                                    en: t('MenuDemoMainCourseDescDemo4', {nb: 4})
                                },
                                entryPrice: {fr: ["17 €"], en: ["€17"]},
                            },
                            {
                                entryId: 4,
                                entryTitle: {
                                    fr: t('MenuDemoMainCourseTitleDemo5', {nb: 5}),
                                    en: t('MenuDemoMainCourseTitleDemo5', {nb: 5})
                                },                                
                                entrySubtitle: {
                                    fr: t('MenuDemoMainCourseDescDemo5', {nb: 5}),
                                    en: t('MenuDemoMainCourseDescDemo5', {nb: 5})
                                },
                                entryPrice: {fr: ["21 €"], en: ["€21"]},
                                // entryImage: "/images/img_burger_bacon.jpg",
                            },
                        ],
                    }
                ],
            },
            {
                pageType: {fr: "Desserts", en: "Desserts"},
                pageTypeImg: "/images/dessert_demo.png",
                pageEntries: [
                    {
                        entryType: "dishList",
                        entries: [
                            {
                                entryId: 0,
                                entryTitle: {
                                    fr: t('MenuDemoDessertTitleDemo1', {nb: 1}),
                                    en: t('MenuDemoDessertTitleDemo1', {nb: 1})
                                },                                
                                entrySubtitle: {
                                    fr: t('MenuDemoDessertDescDemo1', {nb: 1}),
                                    en: t('MenuDemoDessertDescDemo1', {nb: 1})
                                },
                                entryPrice: {fr: ["8 €"], en: ["€8"]},
                                // entryImage: "/images/img_dessert.jpg",
                            },
                            {
                                entryId: 1,
                                entryTitle: {
                                    fr: t('MenuDemoDessertTitleDemo2', {nb: 2}),
                                    en: t('MenuDemoDessertTitleDemo2', {nb: 2})
                                },                                
                                entrySubtitle: {
                                    fr: t('MenuDemoDessertDescDemo2', {nb: 2}),
                                    en: t('MenuDemoDessertDescDemo2', {nb: 2})
                                },
                                entryPrice: {fr: ["8 €"], en: ["€8"]},
                            },
                            {
                                entryId: 2,
                                entryTitle: {
                                    fr: t('MenuDemoDessertTitleDemo3', {nb: 3}),
                                    en: t('MenuDemoDessertTitleDemo3', {nb: 3})
                                },                                
                                entrySubtitle: {
                                    fr: t('MenuDemoDessertDescDemo3', {nb: 3}),
                                    en: t('MenuDemoDessertDescDemo3', {nb: 3})
                                },
                                entryPrice: {fr: ["7 €"], en: ["€7"]},
                                // entryImage: "/images/img_mousse_chocolat.jpg",
                            },
                            {
                                entryId: 3,
                                entryTitle: {
                                    fr: t('MenuDemoDessertTitleDemo4', {nb: 4}),
                                    en: t('MenuDemoDessertTitleDemo4', {nb: 4})
                                },                                
                                entrySubtitle: {
                                    fr: t('MenuDemoDessertDescDemo4', {nb: 4}),
                                    en: t('MenuDemoDessertDescDemo4', {nb: 4})
                                },
                                entryPrice: {fr: ["8 €"], en: ["€8"]},
                            },
                            {
                                entryId: 4,
                                entryTitle: {
                                    fr: t('MenuDemoDessertTitleDemo5', {nb: 5}),
                                    en: t('MenuDemoDessertTitleDemo5', {nb: 5})
                                },                                
                                entrySubtitle: {
                                    fr: t('MenuDemoDessertDescDemo5', {nb: 5}),
                                    en: t('MenuDemoDessertDescDemo5', {nb: 5})
                                },
                                entryPrice: {fr: ["9 €"], en: ["€9"]},
                                // entryImage: "/images/img_glace.jpg",
                            },
                     ],
                    },
                ],
            },
            /*{                
                pageType: {fr: "Vins", en: "Wines"},
                pageEntries: [
                    {
                        entryType: "dishList",
                        entries: [
                            {
                                entryId: 0,
                                entryTitle: {
                                    fr: t('WineDemoTitle', {nb: 1}),
                                    en: t('WineDemoTitle', {nb: 1}),
                                },   
                                entryPrice: {fr:[t('WineDemoPrices')],en:[t('WineDemoPrices')]},
                            },
                            {
                                entryId: 1,
                                entryTitle: {
                                    fr: t('WineDemoTitle', {nb: 2}),
                                    en: t('WineDemoTitle', {nb: 2}),
                                }, 
                                entryPrice: {fr:[t('WineDemoPrices2')],en:[t('WineDemoPrices2')]},
                            },
                            {
                                entryId: 2,
                                entryTitle: {
                                    fr: t('WineDemoTitle', {nb: 3}),
                                    en: t('WineDemoTitle', {nb: 3}),
                                }, 
                                entryPrice: {fr:[t('WineDemoPrices')],en:[t('WineDemoPrices')]},
                            },
                            {
                                entryId: 3,
                                entryTitle: {
                                    fr: t('WineDemoTitle', {nb: 4}),
                                    en: t('WineDemoTitle', {nb: 4}),
                                }, 
                                entryPrice: {fr:[t('WineDemoPrices2')],en:[t('WineDemoPrices2')]},
                            },
                            {
                                entryId: 4,
                                entryTitle: {
                                    fr: t('WineDemoTitle', {nb: 5}),
                                    en: t('WineDemoTitle', {nb: 5}),
                                }, 
                                entryPrice: {fr:[t('WineDemoPrices')],en:[t('WineDemoPrices')]},
                            },
                     ],
                    },
                ],
            },*/
            {                
                pageType: {fr: "Note", en: "Note"},
                pageEntries: [
                    {
                        entryType: "note",
                        text: {
                            fr: t('EndingPageDemoText'),
                            en: t('EndingPageDemoText'),
                        },
                        centering: 'start',
                    },
                ]
            },
        ];
    }

    function getMakerDemoProductPages(){ 
        return [
            /*{
                pageType: {fr: "Menus", en: "Menus"},
                pageEntries: [                 
                    {
                        entryType: "formula",
                        entriesTitle: {
                            fr: t('MenuDemoMenusTitle', {menu: "A"}),
                            en: t('MenuDemoMenusTitle', {menu: "A"}),
                        },                        
                        entries: [
                            {
                                entryId: 0,
                                entryTitle: {
                                    fr: t('MenuDemoStarterTitle', {nb: 1}),
                                    en: t('MenuDemoStarterTitle', {nb: 1})
                                },
                            },
                            {
                                entryId: 1,
                                entryTitle: {
                                    fr: t('MenuDemoStarterTitle', {nb: 2}),
                                    en: t('MenuDemoStarterTitle', {nb: 2})
                                },
                            },
                            {
                                entryId: 2,
                                separator: true,
                            },
                            {
                                entryId: 3,
                                entryTitle: {
                                    fr: t('MenuDemoMainCourseTitle', {nb: 1}),
                                    en: t('MenuDemoMainCourseTitle', {nb: 1})
                                },
                            }, 
                            {
                                entryId: 4,
                                entryTitle: {
                                    fr: t('MenuDemoMainCourseTitle', {nb: 2}),
                                    en: t('MenuDemoMainCourseTitle', {nb: 2})
                                },
                            },  
                            {
                                entryId: 5,
                                separator: true,
                            },
                            {
                                entryId: 6,
                                entryTitle: {
                                    fr: t('MenuDemoDessertTitle', {nb: 1}),
                                    en: t('MenuDemoDessertTitle', {nb: 1}),
                                },
                            }, 
                            {
                                entryId: 7,
                                entryTitle: {
                                    fr: t('MenuDemoDessertTitle', {nb: 2}),
                                    en: t('MenuDemoDessertTitle', {nb: 2}),
                                },
                            },                                 
                        ],                            
                    },
                    {
                        entryType: "formula",
                        entriesTitle: {
                            fr: t('MenuDemoMenusTitle', {menu: "B"}),
                            en: t('MenuDemoMenusTitle', {menu: "B"}),
                        },
                        entries: [
                            {
                                entryId: 0,
                                entryTitle: {
                                    fr: t('MenuDemoMenusDesc1'),
                                    en: t('MenuDemoMenusDesc1'),
                                },                                
                            },
                        ]
                    },                                      
                ]
            },  */         
            {
                pageType: {fr: "Entrées", en: "Starters"},  
                pageTypeImg: "/images/starter_demo.png",                     
                pageEntries: [
                    /*{
                        entryType: "startingNote",
                        text: "starting note",
                        centering: 'center'
                    },*/
                    {
                        entryType: "dishList",                          
                        /*entriesTitle: "Salades",
                        startingNote: "starting note",*/
                        entries: [
                            {
                                entryId: 0,
                                entryTitle: {
                                    fr: t('MenuDemoStarterTitle', {nb: 1}),
                                    en: t('MenuDemoStarterTitle', {nb: 1})
                                },                                
                                entrySubtitle: {
                                    fr: t('MenuDemoStarterDesc', {nb: 1}),
                                    en: t('MenuDemoStarterDesc', {nb: 1})
                                },
                                entryPrice: {fr: ["X €"], en: ["X €"]},
                                // entryImage: "/images/img_starter.jpg",
                            },
                            {
                                entryId: 1,
                                entryTitle: {
                                    fr: t('MenuDemoStarterTitle', {nb: 2}),
                                    en: t('MenuDemoStarterTitle', {nb: 2})
                                },                                
                                entrySubtitle: {
                                    fr: t('MenuDemoStarterDesc', {nb: 2}),
                                    en: t('MenuDemoStarterDesc', {nb: 2})
                                },  
                                entryPrice: {fr: ["X €"], en: ["X €"]},                          
                            },
                            {
                                entryId: 2,
                                entryTitle: {
                                    fr: t('MenuDemoStarterTitle', {nb: 3}),
                                    en: t('MenuDemoStarterTitle', {nb: 3})
                                },                                
                                entrySubtitle: {
                                    fr: t('MenuDemoStarterDesc', {nb: 3}),
                                    en: t('MenuDemoStarterDesc', {nb: 3})
                                }, 
                                entryPrice: {fr: ["X €"], en: ["X €"]},
                                // entryImage: "/images/img_starter.jpg",
                            },
                            {
                                entryId: 3,
                                entryTitle: {
                                    fr: t('MenuDemoStarterTitle', {nb: 4}),
                                    en: t('MenuDemoStarterTitle', {nb: 4})
                                },                                
                                entrySubtitle: {
                                    fr: t('MenuDemoStarterDesc', {nb: 4}),
                                    en: t('MenuDemoStarterDesc', {nb: 4})
                                },
                                entryPrice: {fr: ["X €"], en: ["X €"]},
                            },
                            {
                                entryId: 4,
                                entryTitle: {
                                    fr: t('MenuDemoStarterTitle', {nb: 5}),
                                    en: t('MenuDemoStarterTitle', {nb: 5})
                                },                                
                                entrySubtitle: {
                                    fr: t('MenuDemoStarterDesc', {nb: 5}),
                                    en: t('MenuDemoStarterDesc', {nb: 5})
                                },
                                entryPrice: {fr: ["X €"], en: ["X €"]},
                                // entryImage: "/images/img_starter.jpg",
                            },                    
                        ],
                        /*endingNote: "ending note",*/
                    },
                    /*{
                        entryType: "dishList",
                        entriesTitle: "Antipasti",
                        startingNote: "starting note",                        
                        entries: [
                            {
                                entryId: 0,
                                entryTitle: t('MenuDemoStarterTitle', {nb: 1}),
                                entrySubtitle: t('MenuDemoStarterDesc', {nb: 1}),
                                entryPrice: {fr:["37.5 cl : X €", "75 cl : X €"],en:["37.5 cl : X €", "75 cl : X €"]},
                                entryImage: "/images/img_starter.jpg",
                            },
                            {
                                entryId: 1,
                                entryTitle: t('MenuDemoStarterTitle', {nb: 2}),
                                entrySubtitle: t('MenuDemoStarterDesc', {nb: 2}),                                
                            },
                            {
                                entryId: 2,
                                entryTitle: t('MenuDemoStarterTitle', {nb: 3}),
                                entrySubtitle: t('MenuDemoStarterDesc', {nb: 3}),
                                entryPrice: {fr: ["X €"], en: ["X €"]},
                                entryImage: "/images/img_starter.jpg",
                            },
                            {
                                entryId: 3,
                                entryTitle: t('MenuDemoStarterTitle', {nb: 4}),
                                entrySubtitle: t('MenuDemoStarterDesc', {nb: 4}),
                                entryPrice: {fr: ["X €"], en: ["X €"]},
                            },
                            {
                                entryId: 4,
                                entryTitle: t('MenuDemoStarterTitle', {nb: 5}),
                                entrySubtitle: t('MenuDemoStarterDesc', {nb: 5}),
                                entryPrice: {fr: ["X €"], en: ["X €"]},
                                entryImage: "/images/img_starter.jpg",
                            },                    
                        ],
                        endingNote: "ending note",                        
                    },
                    {
                        entryType: "endingNote",
                        text: "ending note",
                        centering: 'center'
                    },*/
                ]
            },
            {
                pageType: {fr: "Plats", en: "Dishes"},
                pageEntries: [
                    {
                        entryType: "dishList",
                        entries: [
                            {
                                entryId: 0,
                                entryTitle: {
                                    fr: t('MenuDemoMainCourseTitle', {nb: 1}),
                                    en: t('MenuDemoMainCourseTitle', {nb: 1})
                                },                                
                                entrySubtitle: {
                                    fr: t('MenuDemoMainCourseDesc', {nb: 1}),
                                    en: t('MenuDemoMainCourseDesc', {nb: 1})
                                },
                                entryPrice: {fr: ["X €"], en: ["X €"]},
                                signature: true,
                                // entryImage: "/images/img_maincourse.jpg",
                            },
                            {
                                entryId: 1,
                                entryTitle: {
                                    fr: t('MenuDemoMainCourseTitle', {nb: 2}),
                                    en: t('MenuDemoMainCourseTitle', {nb: 2})
                                },                                
                                entrySubtitle: {
                                    fr: t('MenuDemoMainCourseDesc', {nb: 2}),
                                    en: t('MenuDemoMainCourseDesc', {nb: 2})
                                },
                                entryPrice: {fr: ["X €"], en: ["X €"]},
                            },
                            {
                                entryId: 2,
                                entryTitle: {
                                    fr: t('MenuDemoMainCourseTitle', {nb: 3}),
                                    en: t('MenuDemoMainCourseTitle', {nb: 3})
                                },                                
                                entrySubtitle: {
                                    fr: t('MenuDemoMainCourseDesc', {nb: 3}),
                                    en: t('MenuDemoMainCourseDesc', {nb: 3})
                                },
                                entryPrice: {fr: ["X €"], en: ["X €"]},
                                // entryImage: "/images/img_maincourse.jpg",
                            },
                            {
                                entryId: 3,
                                entryTitle: {
                                    fr: t('MenuDemoMainCourseTitle', {nb: 4}),
                                    en: t('MenuDemoMainCourseTitle', {nb: 4})
                                },                                
                                entrySubtitle: {
                                    fr: t('MenuDemoMainCourseDesc', {nb: 4}),
                                    en: t('MenuDemoMainCourseDesc', {nb: 4})
                                },
                                entryPrice: {fr: ["X €"], en: ["X €"]},
                            },
                            {
                                entryId: 4,
                                entryTitle: {
                                    fr: t('MenuDemoMainCourseTitle', {nb: 5}),
                                    en: t('MenuDemoMainCourseTitle', {nb: 5})
                                },                                
                                entrySubtitle: {
                                    fr: t('MenuDemoMainCourseDesc', {nb: 5}),
                                    en: t('MenuDemoMainCourseDesc', {nb: 5})
                                },
                                entryPrice: {fr: ["X €"], en: ["X €"]},
                                // entryImage: "/images/img_maincourse.jpg",
                            },
                        ],
                    }
                ],
            },
            {
                pageType: {fr: "Desserts", en: "Desserts"},
                pageEntries: [
                    {
                        entryType: "dishList",
                        entries: [
                            {
                                entryId: 0,
                                entryTitle: {
                                    fr: t('MenuDemoDessertTitle', {nb: 1}),
                                    en: t('MenuDemoDessertTitle', {nb: 1})
                                },                                
                                entrySubtitle: {
                                    fr: t('MenuDemoDessertDesc', {nb: 1}),
                                    en: t('MenuDemoDessertDesc', {nb: 1})
                                },
                                entryPrice: {fr: ["X €"], en: ["X €"]},
                                // entryImage: "/images/img_dessert.jpg",
                            },
                            {
                                entryId: 1,
                                entryTitle: {
                                    fr: t('MenuDemoDessertTitle', {nb: 2}),
                                    en: t('MenuDemoDessertTitle', {nb: 2})
                                },                                
                                entrySubtitle: {
                                    fr: t('MenuDemoDessertDesc', {nb: 2}),
                                    en: t('MenuDemoDessertDesc', {nb: 2})
                                },
                                entryPrice: {fr: ["X €"], en: ["X €"]},
                            },
                            {
                                entryId: 2,
                                entryTitle: {
                                    fr: t('MenuDemoStarterTitle', {nb: 3}),
                                    en: t('MenuDemoStarterTitle', {nb: 3})
                                },                                
                                entrySubtitle: {
                                    fr: t('MenuDemoDessertDesc', {nb: 3}),
                                    en: t('MenuDemoDessertDesc', {nb: 3})
                                },
                                entryPrice: {fr: ["X €"], en: ["X €"]},
                                // entryImage: "/images/img_dessert.jpg",
                            },
                            {
                                entryId: 3,
                                entryTitle: {
                                    fr: t('MenuDemoDessertTitle', {nb: 4}),
                                    en: t('MenuDemoDessertTitle', {nb: 4})
                                },                                
                                entrySubtitle: {
                                    fr: t('MenuDemoDessertTitle', {nb: 4}),
                                    en: t('MenuDemoDessertTitle', {nb: 4})
                                },
                                entryPrice: {fr: ["X €"], en: ["X €"]},
                            },
                            {
                                entryId: 4,
                                entryTitle: {
                                    fr: t('MenuDemoStarterTitle', {nb: 5}),
                                    en: t('MenuDemoStarterTitle', {nb: 5})
                                },                                
                                entrySubtitle: {
                                    fr: t('MenuDemoDessertTitle', {nb: 5}),
                                    en: t('MenuDemoDessertTitle', {nb: 5})
                                },
                                entryPrice: {fr: ["X €"], en: ["X €"]},
                                // entryImage: "/images/img_dessert.jpg",
                            },
                     ],
                    },
                ],
            },
            /*{
                pageType: {fr: "Vins", en: "Wines"},
                pageEntries: [
                    {
                        entryType: "dishList",
                        entries: [
                            {
                                entryId: 0,
                                entryTitle: {
                                    fr: t('WineDemoTitle', {nb: 1}),
                                    en: t('WineDemoTitle', {nb: 1}),
                                },   
                                entryPrice: {fr: [t('WineDemoPrices')], en: [t('WineDemoPrices')]},
                            },
                            {
                                entryId: 1,
                                entryTitle: {
                                    fr: t('WineDemoTitle', {nb: 2}),
                                    en: t('WineDemoTitle', {nb: 2}),
                                }, 
                                entryPrice: {fr:[t('WineDemoPrices2')],en:[t('WineDemoPrices2')]},
                            },
                            {
                                entryId: 2,
                                entryTitle: {
                                    fr: t('WineDemoTitle', {nb: 3}),
                                    en: t('WineDemoTitle', {nb: 3}),
                                }, 
                                entryPrice: {fr: [t('WineDemoPrices')], en: [t('WineDemoPrices')]},
                            },
                            {
                                entryId: 3,
                                entryTitle: {
                                    fr: t('WineDemoTitle', {nb: 4}),
                                    en: t('WineDemoTitle', {nb: 4}),
                                }, 
                                entryPrice: {fr:[t('WineDemoPrices2')],en:[t('WineDemoPrices2')]},
                            },
                            {
                                entryId: 4,
                                entryTitle: {
                                    fr: t('WineDemoTitle', {nb: 5}),
                                    en: t('WineDemoTitle', {nb: 5}),
                                }, 
                                entryPrice: {fr: [t('WineDemoPrices')], en: [t('WineDemoPrices')]},
                            },
                     ],
                    },
                ],
            },*/
            {
                pageType: {fr: "Note", en: "Note"},
                pageEntries: [
                    {
                        entryType: "note",
                        text: {
                            fr: t('EndingPageDemoText'),
                            en: t('EndingPageDemoText'),
                        },
                        centering: 'start',
                    },
                ]
            },
        ];
    }
}

export function getEmojiFromPageType(pageType){
    switch (pageType){
        case "Starters":
            return "🥗";
        case "Dishes":
            return "🥘";
        case "Desserts":
            return "🍰"; 
        case "Wines":
            return "🍷";
        case "Drinks":
            return "🍹";
        case "Menus":
            return "🍽️";
        case "Note":
            return "👋";
            
        default:
            return "";
    }
}

export default Menu;